import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useSubscription } from '../contexts/SubscriptionContext';
import LockedPage from '../components/LockedPage';
import * as pdfjsLib from 'pdfjs-dist';
import { jsPDF } from 'jspdf';

// CDN worker — version must match installed pdfjs-dist
pdfjsLib.GlobalWorkerOptions.workerSrc =
  'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';

/* ── Utilities ─────────────────────────────────────────────────────────── */

function normalizeDate(str) {
  if (!str) return null;
  str = String(str).trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(str)) return str;
  const m = str.match(/^(\d{1,2})[.\-\/](\d{1,2})[.\-\/](\d{2,4})$/);
  if (m) {
    let [, d, mo, y] = m;
    if (y.length === 2) {
      // Years > 30 are 19xx (e.g. 84 → 1984), ≤ 30 are 20xx (e.g. 26 → 2026)
      y = (parseInt(y, 10) > 30 ? '19' : '20') + y;
    }
    return `${y}-${mo.padStart(2, '0')}-${d.padStart(2, '0')}`;
  }
  return str;
}

function normSurname(name) {
  if (!name) return '';
  return name.toLowerCase()
    .replace(/\s+/g, '').replace(/-/g, '')
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '');
}

function normFirst(name) {
  if (!name) return '';
  return name.toLowerCase()
    .replace(/[()]/g, ' ').replace(/\s+/g, ' ').trim()
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '');
}

function normalizeBoardType(raw) {
  if (!raw) return null;
  const r = raw.toLowerCase().trim();
  if (r === 'ai' || r.includes('all incl') || r.includes('allincl')) return 'All Inclusive';
  if (r === 'logies' || r === 'lo' || r === 'lb' || r.includes('logies')) return 'Logies';
  if (r.includes('half pens') || r === 'hp' || r === 'hb') return 'Half Pension';
  if (r.includes('vol pens') || r === 'vp' || r === 'fb') return 'Vol Pension';
  if (r === 'bb' || (r.includes('bed') && r.includes('break'))) return 'Bed & Breakfast';
  return raw.trim();
}

function parsePrice(str) {
  if (str == null) return null;
  if (typeof str === 'number') return str;
  let s = String(str).replace(/[€\s]/g, '').trim();
  // Dutch format: 1.670,39 → comma is decimal, dot is thousands separator
  if (/,\d{2}$/.test(s)) {
    s = s.replace(/\./g, '').replace(',', '.');
  }
  // English/BAS format: 1670.39 → dot is already decimal, nothing to strip
  return parseFloat(s) || null;
}

/* ── PDF text extraction (client-side, pdfjs) ──────────────────────────── */

async function extractPDFText(file) {
  const arrayBuffer = await file.arrayBuffer();
  const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
  let fullText = '';
  for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
    const page = await pdf.getPage(pageNum);
    const content = await page.getTextContent();
    let lastY = null;
    let line = '';
    const lines = [];
    for (const item of content.items) {
      const y = Math.round(item.transform[5]);
      if (lastY !== null && Math.abs(y - lastY) > 4) {
        if (line.trim()) lines.push(line.trim());
        line = item.str;
      } else {
        line += (line ? ' ' : '') + item.str;
      }
      lastY = y;
    }
    if (line.trim()) lines.push(line.trim());
    fullText += lines.join('\n') + '\n';
  }
  if (fullText.replace(/\s/g, '').length < 50) {
    throw new Error(
      'Dit PDF bevat geen leesbare tekst. Gescande PDF\'s en afbeeldingen worden niet ondersteund. Gebruik een tekst-gebaseerd PDF.'
    );
  }
  return fullText;
}

/* ── BAS Parser (Fadiro/agency booking confirmation) ───────────────────── */

function parseBAS(raw) {
  if (!raw.includes('Boekingsbevestiging') && !raw.includes('Factuur:')) return null;

  const result = {
    travelers: [], destination: null, hotel: null,
    departureDate: null, returnDate: null, duration: null,
    outboundFlight: null, outboundDep: null, outboundArr: null,
    returnFlight: null, returnDep: null, returnArr: null,
    boardType: null, roomType: null, totalPrice: null,
    baggageIncluded: false, transferIncluded: false,
    reservationNumber: null, pnr: null, _format: 'BAS',
  };

  const lines = raw.split('\n').map(l => l.trim()).filter(Boolean);

  // Travelers: "1 Mw. T.W. (Trynke Wytske) de Haan 01-02-1984 NL ..."
  const travRe = /^(\d+)\s+(?:Dhr\.?|Mw\.?|De heer|Mevrouw)\s+(.+?)\s+(\d{2}-\d{2}-\d{4})\s+[A-Z]{2}/;
  for (const line of lines) {
    const m = line.match(travRe);
    if (m) {
      const namePart = m[2].trim();
      // Extract full first name from parentheses if present: "T.W. (Trynke Wytske) de Haan"
      const parenM = namePart.match(/\(([^)]+)\)/);
      const fullFirst = parenM ? parenM[1] : null;
      const withoutParens = namePart.replace(/\([^)]+\)/, '').replace(/\s+/g, ' ').trim();
      const parts = withoutParens.split(/\s+/);
      const initParts = [], surnameParts = [];
      let inSurname = false;
      for (const p of parts) {
        // Match compound initials like "T.W." or "I." — one or more capital+dot sequences
        if (!inSurname && /^([A-Z]\.)+$/.test(p)) {
          initParts.push(p);
        } else {
          inSurname = true;
          surnameParts.push(p);
        }
      }
      result.travelers.push({
        firstName: fullFirst || initParts.join(' '),
        lastName: surnameParts.join(' ') || parts[parts.length - 1],
        dob: normalizeDate(m[3]),
      });
    }
  }

  // Reservation number
  const resM = raw.match(/Reserveringsnummer\s*:\s*(\w+)/);
  if (resM) result.reservationNumber = resM[1];

  // PNR
  const pnrM = raw.match(/PNR\s+([A-Z0-9]{6})/);
  if (pnrM) result.pnr = pnrM[1];

  // Dates from the booking summary block
  const beginM = raw.match(/Begindatum\s*:\s*(\d{2}-\d{2}-\d{4})/);
  const eindM = raw.match(/Einddatum\s*:\s*(\d{2}-\d{2}-\d{4})/);
  if (beginM) result.departureDate = normalizeDate(beginM[1]);
  if (eindM) result.returnDate = normalizeDate(eindM[1]);

  // Duration
  const duurM = raw.match(/Reisduur\s*\(dagen\)\s*:\s*(\d+)/);
  if (duurM) result.duration = parseInt(duurM[1]);

  // Destination
  const destM = raw.match(/Definitieve boeking,\s*Pakketreis\s+([^\n\r]+)/);
  if (destM) result.destination = destM[1].trim();

  // Flights: line-by-line approach for reliability
  const flightBlocks = [];
  for (let i = 0; i < lines.length; i++) {
    const vertrekM = lines[i].match(/Vertrek:\s*(\d{2}:\d{2})u[^\n]*([A-Z]{2}\s*\d{3,4})/);
    if (vertrekM) {
      let arr = null;
      // Look at next 1–2 lines for Aankomst
      for (let j = i + 1; j <= Math.min(i + 2, lines.length - 1); j++) {
        const aankomstM = lines[j].match(/Aankomst:\s*(\d{2}:\d{2})u/);
        if (aankomstM) { arr = aankomstM[1]; break; }
      }
      flightBlocks.push({ dep: vertrekM[1], flight: vertrekM[2].replace(/\s+/, ' ').trim(), arr });
    }
  }
  if (flightBlocks[0]) {
    result.outboundFlight = flightBlocks[0].flight;
    result.outboundDep = flightBlocks[0].dep;
    result.outboundArr = flightBlocks[0].arr;
  }
  if (flightBlocks[1]) {
    result.returnFlight = flightBlocks[1].flight;
    result.returnDep = flightBlocks[1].dep;
    result.returnArr = flightBlocks[1].arr;
  }

  // Hotel: "7 nachten Hotel Duas Torres Funchal"
  const hotelM = raw.match(/(\d+)\s+nachten\s+([^\n]+)/i);
  if (hotelM) result.hotel = hotelM[2].trim();

  // Board type
  const verzM = raw.match(/Verzorging\s*:\s*([^\n]+)/);
  if (verzM) result.boardType = normalizeBoardType(verzM[1].trim());

  // Room type
  const typeM = raw.match(/Type\s*:\s*(?:\d+\s*x\s*)?([^\n]+)/);
  if (typeM) result.roomType = typeM[1].trim();

  // Price — package subtotal (what operator charges, excl. insurance/fees)
  const pkgM = raw.match(/Subtotaal pakketreis:\s*€?\s*([\d.,]+)/);
  if (pkgM) result.totalPrice = parsePrice(pkgM[1]);

  // Baggage in price spec
  result.baggageIncluded = /Ruimbagage|ruimbagage|20\s*KG|23\s*KG/i.test(raw);

  // Transfer
  result.transferIncluded =
    /bustransfer|privétransfer|shuttle/i.test(raw) &&
    !/geen\s+bustransfer|geen\s+transfer/i.test(raw);

  return result;
}

/* ── Corendon Parser (fixed-width monospace PDF) ───────────────────────── */

function parseCorendon(raw) {
  if (!/BEVESTIGING EN FACTUUR|BOEKINGSNUMMER/i.test(raw)) return null;

  const result = {
    travelers: [], destination: null, hotel: null,
    departureDate: null, returnDate: null, duration: null,
    outboundFlight: null, outboundDep: null, outboundArr: null,
    returnFlight: null, returnDep: null, returnArr: null,
    boardType: null, roomType: null, totalPrice: null,
    baggageIncluded: false, transferIncluded: false,
    reservationNumber: null, pnr: null, _format: 'Corendon',
  };

  const bookM = raw.match(/BOEKINGSNUMMER\s*:\s*(\d+)/i);
  if (bookM) result.reservationNumber = bookM[1];

  const pnrM = raw.match(/PNR:\s*([A-Z0-9]{6})/i);
  if (pnrM) result.pnr = pnrM[1];

  // Travelers: "1 MEVR DEHAAN, TRYNKE 01.02.84"
  // Note: Corendon uses en-dash (−) and regular hyphen
  const travRe = /^(\d+)\s+(?:MEVR\.?|DHR\.?|DHRM\.?|MSTR\.?)\s+([A-Z\u2212\-]+)[,\s]+([A-Z\-]+)\s+(\d{2}[.\-]\d{2}[.\-]\d{2,4})/gm;
  let tm;
  while ((tm = travRe.exec(raw)) !== null) {
    result.travelers.push({
      lastName: tm[2].replace(/\u2212/g, ''), // strip en-dash artifacts
      firstName: tm[3],
      dob: normalizeDate(tm[4]),
    });
  }

  // Destination
  const destM = raw.match(/PAKKETREIS\s+([A-Z][A-Z\s]+?)(?:\n|$)/i);
  if (destM) result.destination = destM[1].trim();

  // Outbound/return flight dates (no times in Corendon PDF extract)
  const heenM = raw.match(/HEENVLUCHT\s+(\d{2}[.\-]\d{2}[.\-]\d{2,4})/i);
  const terugM = raw.match(/TERUGVLUCHT\s+(\d{2}[.\-]\d{2}[.\-]\d{2,4})/i);
  if (heenM) result.departureDate = normalizeDate(heenM[1]);
  if (terugM) result.returnDate = normalizeDate(terugM[1]);

  // Duration
  const nachtenM = raw.match(/(\d+)\s+NACHTEN/i);
  if (nachtenM) result.duration = parseInt(nachtenM[1]) + 1;

  // Hotel: "7 NACHTEN Duas Torres Funchal / Madeira"
  const hotelM = raw.match(/NACHTEN\s+(.+?)(?:\s*\/\s*\S+)?\s*\n/i);
  if (hotelM) result.hotel = hotelM[1].trim();

  // From/To for fallback dates
  const vanTotM = raw.match(/VAN\s+(\d{2}[.\-]\d{2}[.\-]\d{2,4})\s+TOT\s+(\d{2}[.\-]\d{2}[.\-]\d{2,4})/i);
  if (vanTotM) {
    if (!result.departureDate) result.departureDate = normalizeDate(vanTotM[1]);
    if (!result.returnDate) result.returnDate = normalizeDate(vanTotM[2]);
  }

  // Board type + room: "1 2-PERS. STUDIO ZIJZEEZICHT LOGIES 1.310,00 EUR"
  const roomLineM = raw.match(
    /\d+\s+(.+?)\s+(LOGIES|ALL[\s\-]*INCLUSIVE|ALLINCLUSIEF|HALF\s*PENSION|VOL\s*PENSION|AI|HP|BB|FB|VP)\s+[\d.,]+\s+EUR/i
  );
  if (roomLineM) {
    result.roomType = roomLineM[1].trim();
    result.boardType = normalizeBoardType(roomLineM[2]);
  }

  result.baggageIncluded =
    /Bagage heen en terug|ruimbagage/i.test(raw) && !/Alleen handbagage/i.test(raw);
  result.transferIncluded =
    /bustransfer/i.test(raw) && !/geen\s*bustransfer/i.test(raw);

  const totalM = raw.match(/TOTAAL\s+([\d.]+,\d{2})\s+EUR/i);
  if (totalM) result.totalPrice = parsePrice(totalM[1]);

  return result;
}

/* ── TUI XML Parser (RecapResponse) ────────────────────────────────────── */

function parseTUIXML(xmlStr) {
  let doc;
  try {
    doc = new DOMParser().parseFromString(xmlStr, 'application/xml');
  } catch { return null; }
  if (doc.querySelector('parsererror')) return null;

  // Helper: walk tag path using getElementsByTagName
  function xGet(el, ...tags) {
    let cur = el;
    for (const tag of tags) {
      if (!cur) return null;
      cur = cur.getElementsByTagName(tag)[0] ?? null;
    }
    return cur?.textContent?.trim() ?? null;
  }
  function xAll(el, tag) {
    return Array.from(el?.getElementsByTagName(tag) ?? []);
  }

  const result = {
    travelers: [], destination: null, hotel: null,
    departureDate: null, returnDate: null, duration: null,
    outboundFlight: null, outboundDep: null, outboundArr: null,
    returnFlight: null, returnDep: null, returnArr: null,
    boardType: null, roomType: null, totalPrice: null,
    baggageIncluded: false, transferIncluded: false,
    reservationNumber: xGet(doc, 'PackageID') || xGet(doc, 'BookID'),
    pnr: null, _format: 'TUI',
  };

  result.departureDate = xGet(doc, 'DepartureDate');
  const dur = parseInt(xGet(doc, 'Duration') || '0');
  result.duration = dur;

  // Outbound
  const tranTo = doc.getElementsByTagName('TransportTo')[0];
  if (tranTo) {
    result.outboundFlight = xGet(tranTo, 'FlightNumber');
    const dep = tranTo.getElementsByTagName('Departure')[0];
    result.outboundDep = xGet(dep, 'Time');
    const arr = tranTo.getElementsByTagName('Arrival')[0];
    result.outboundArr = xGet(arr, 'Time');
  }

  // Return
  const tranFrom = doc.getElementsByTagName('TransportFrom')[0];
  if (tranFrom) {
    result.returnFlight = xGet(tranFrom, 'FlightNumber');
    const dep = tranFrom.getElementsByTagName('Departure')[0];
    result.returnDep = xGet(dep, 'Time');
    result.returnDate = xGet(dep, 'Date'); // date passenger boards return flight
    const arr = tranFrom.getElementsByTagName('Arrival')[0];
    result.returnArr = xGet(arr, 'Time');
  }

  // Accommodation
  const acco = doc.getElementsByTagName('Accommodation')[0];
  if (acco) {
    result.hotel = xGet(acco, 'Name');
    const place = xGet(acco, 'Place');
    const country = xGet(acco, 'Country');
    result.destination = [place, country].filter(Boolean).join(', ');
    const board = acco.getElementsByTagName('Board')[0];
    result.boardType = normalizeBoardType(
      xGet(board, 'BoardType') || xGet(board, 'Description')
    );
    const unit = acco.getElementsByTagName('Unit')[0];
    result.roomType = xGet(unit, 'Description') || null;
  }

  // Travelers
  xAll(doc, 'PaxDescription').forEach(pax => {
    result.travelers.push({
      lastName: xGet(pax, 'Name'),
      firstName: xGet(pax, 'FirstName'),
      dob: xGet(pax, 'DateOfBirth'),
    });
  });

  // Baggage and transfer from extras (deduplicated by ExtraID)
  const seen = new Set();
  xAll(doc, 'Extras').forEach(extra => {
    const id = xGet(extra, 'ExtraID');
    if (seen.has(id)) return;
    seen.add(id);
    const desc = (xGet(extra, 'Description') || '').toLowerCase();
    if (desc.includes('ruimbagage') || desc.includes('20 kg') || desc.includes('23 kg')) {
      result.baggageIncluded = true;
    }
    if (desc.includes('transfer') || desc.includes('shuttle')) {
      result.transferIncluded = true;
    }
  });

  // Price — TUI stores in eurocents (e.g. 689100 = €6891.00)
  const priceInfo = doc.getElementsByTagName('PriceInfo')[0];
  const priceEl = priceInfo?.getElementsByTagName('Price')[0];
  if (priceEl) {
    const val = parseInt(priceEl.textContent.trim());
    result.totalPrice = val > 10000 ? val / 100 : val;
  }

  return result;
}

/* ── Airport normalization ─────────────────────────────────────────────── */

const AIRPORT_ALIAS_MAP = [
  [['amsterdam', 'schiphol', 'amsterdam schiphol', 'amsterdam airport schiphol', 'ams'], 'ams'],
  [['antalya', 'ayt'], 'ayt'],
  [['dubai', 'dxb'], 'dxb'],
  [['hurghada', 'hrg'], 'hrg'],
  [['sharm el sheikh', 'ssh'], 'ssh'],
  [['rhodes', 'rhodos', 'rho'], 'rho'],
  [['heraklion', 'heraklyon', 'kreta', 'crete', 'her'], 'her'],
  [['palma', 'palma de mallorca', 'mallorca', 'pmi'], 'pmi'],
  [['lanzarote', 'arrecife', 'ace'], 'ace'],
  [['fuerteventura', 'fue'], 'fue'],
  [['gran canaria', 'las palmas', 'lpa'], 'lpa'],
  [['tenerife', 'tenerife sur', 'tfs'], 'tfs'],
  [['funchal', 'madeira', 'fnc'], 'fnc'],
  [['alicante', 'alc'], 'alc'],
  [['ibiza', 'ibz'], 'ibz'],
  [['menorca', 'mah'], 'mah'],
];

function normalizeAirport(s) {
  if (!s) return '';
  const v = s.toLowerCase().replace(/\s+/g, ' ').trim();
  for (const [aliases, canonical] of AIRPORT_ALIAS_MAP) {
    if (aliases.includes(v)) return canonical;
  }
  return v;
}

/* ── Room type normalization ───────────────────────────────────────────── */

function normalizeRoomType(s) {
  if (!s) return '';
  return String(s).toLowerCase()
    .replace(/\b(standaard|standard|superior|deluxe|classic|comfort|premium|executive|budget)\b/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

/* ── Name comparison ───────────────────────────────────────────────────── */

function compareNames(a, b) {
  const lastA = normSurname(a.lastName);
  const lastB = normSurname(b.lastName);

  if (!lastA || !lastB) return { severity: 'warning', detail: 'Ontbrekende achternaam' };

  if (lastA !== lastB) {
    return { severity: 'error', detail: `Achternaam: "${a.lastName}" ≠ "${b.lastName}"` };
  }

  const firstA = normFirst(a.firstName);
  const firstB = normFirst(b.firstName);
  if (firstA === firstB) return { severity: 'correct', detail: null };

  // Compare initials: "t.w." → ['t','w'],  "trynke wytske" → ['t','w']
  const getInits = s => s.replace(/\./g, ' ').split(/\s+/).filter(Boolean).map(w => w[0]);
  const inA = getInits(firstA);
  const inB = getInits(firstB);

  if (inA.join('') === inB.join('') && inA.length > 0) {
    // Initials match but full names differ — uncertain: needs DOB to fully resolve
    return {
      severity: 'uncertain',
      detail: `Initialen overeenkomen, voornaam niet volledig bevestigd: "${a.firstName}" vs "${b.firstName}"`,
    };
  }
  if (inA[0] === inB[0]) {
    return { severity: 'warning', detail: `Voornaam: "${a.firstName}" vs "${b.firstName}"` };
  }
  return { severity: 'warning', detail: `Voornaam afwijking: "${a.firstName}" vs "${b.firstName}"` };
}

/* ── Comparison logic ──────────────────────────────────────────────────── */

function compareBookings(left, right) {
  const diff = {};

  function normalizeHotel(s) {
    return String(s).toLowerCase().trim()
      .replace(/^(hotel|resort|apartments?|apart(hotel)?|suite[s]?|villa[s]?)\s+/i, '')
      .replace(/\s+/g, ' ');
  }

  // tier 1 = error on mismatch/missing (names, dates, flights, hotel structural)
  // tier 2 = warning on mismatch (destination, board, room, duration)
  // tier 3 = warning on mismatch, threshold-based (price, baggage, transfer)
  function field(key, lv, rv, type = 'string', tier = 2) {
    if (lv == null && rv == null) {
      diff[key] = { severity: 'correct', left: lv, right: rv, detail: null };
      return;
    }
    if (lv == null || rv == null) {
      diff[key] = {
        severity: tier === 1 ? 'error' : 'warning',
        left: lv, right: rv,
        detail: lv == null ? 'Ontbreekt in BAS' : 'Ontbreekt bij operator',
      };
      return;
    }

    let match;
    let detail = null;

    if (type === 'date') {
      match = normalizeDate(String(lv)) === normalizeDate(String(rv));
    } else if (type === 'price') {
      const lp = parsePrice(lv), rp = parsePrice(rv);
      match = lp != null && rp != null && Math.abs(lp - rp) <= 0.5;
      if (!match && lp != null && rp != null)
        detail = `Verschil: €${Math.abs(lp - rp).toFixed(2)}`;
    } else if (type === 'bool') {
      match = lv === rv;
    } else if (type === 'hotel') {
      const nl = normalizeHotel(lv), nr = normalizeHotel(rv);
      match = nl === nr || nl.includes(nr) || nr.includes(nl);
    } else if (type === 'room') {
      match = normalizeRoomType(lv) === normalizeRoomType(rv);
    } else if (type === 'airport') {
      match = normalizeAirport(lv) === normalizeAirport(rv);
    } else if (type === 'flight') {
      const norm = s => String(s).replace(/\s+/g, '').toUpperCase();
      match = norm(lv) === norm(rv);
    } else {
      match = String(lv).toLowerCase().trim() === String(rv).toLowerCase().trim();
    }

    diff[key] = {
      severity: match ? 'correct' : tier === 1 ? 'error' : 'warning',
      left: lv, right: rv,
      detail,
    };
  }

  // ── Travelers — sort by normalized surname before pairing ───────────────
  const lT = [...(left.travelers || [])].sort((a, b) => normSurname(a.lastName).localeCompare(normSurname(b.lastName)));
  const rT = [...(right.travelers || [])].sort((a, b) => normSurname(a.lastName).localeCompare(normSurname(b.lastName)));
  const maxLen = Math.max(lT.length, rT.length);
  const travDetails = [];

  for (let i = 0; i < maxLen; i++) {
    const lt = lT[i], rt = rT[i];
    // Always-error structural cases
    if (!lt) { travDetails.push({ severity: 'error', left: null, right: rt, detail: 'Extra reiziger bij operator' }); continue; }
    if (!rt) { travDetails.push({ severity: 'error', left: lt, right: null, detail: 'Reiziger ontbreekt bij operator' }); continue; }

    const nameRes = compareNames(lt, rt);
    let severity = nameRes.severity;
    let detail = nameRes.detail;

    if (lt.dob && rt.dob) {
      if (normalizeDate(lt.dob) !== normalizeDate(rt.dob)) {
        // DOB mismatch is always an error
        severity = 'error';
        detail = `Geboortedatum: ${lt.dob} ≠ ${rt.dob}`;
      } else if (severity === 'uncertain') {
        // Matching DOB resolves name uncertainty
        severity = 'correct';
        detail = null;
      }
    } else if (lt.dob || rt.dob) {
      // DOB missing on one side — if name is otherwise correct, flag as warning
      if (severity === 'correct') {
        severity = 'warning';
        detail = 'Geboortedatum ontbreekt aan één zijde';
      }
      // If already uncertain/warning/error, keep as-is
    }

    travDetails.push({ severity, left: lt, right: rt, detail });
  }

  const sevRank = s => ({ error: 3, uncertain: 2, warning: 1, correct: 0 }[s] ?? 0);
  const travOverall = travDetails.reduce(
    (acc, t) => sevRank(t.severity) > sevRank(acc) ? t.severity : acc,
    'correct'
  );
  diff.travelers = { severity: travOverall, details: travDetails };

  // ── Fields ──────────────────────────────────────────────────────────────
  // Tier 1 = error on mismatch   Tier 2 = warning   Tier 3 = warning (threshold)
  field('destination',      left.destination,      right.destination,      'airport', 2);
  field('hotel',            left.hotel,            right.hotel,            'hotel',   1); // structural: missing = error
  field('departureDate',    left.departureDate,    right.departureDate,    'date',    1);
  field('returnDate',       left.returnDate,       right.returnDate,       'date',    1);
  field('duration',         left.duration,         right.duration,         'string',  2);
  field('outboundFlight',   left.outboundFlight,   right.outboundFlight,   'flight',  1);
  field('outboundDep',      left.outboundDep,      right.outboundDep,      'string',  1);
  field('outboundArr',      left.outboundArr,      right.outboundArr,      'string',  1);
  field('returnFlight',     left.returnFlight,     right.returnFlight,     'flight',  1);
  field('returnDep',        left.returnDep,        right.returnDep,        'string',  1);
  field('returnArr',        left.returnArr,        right.returnArr,        'string',  1);
  field('boardType',        left.boardType,        right.boardType,        'string',  2);
  field('roomType',         left.roomType,         right.roomType,         'room',    2);
  field('totalPrice',       left.totalPrice,       right.totalPrice,       'price',   3);
  field('baggageIncluded',  left.baggageIncluded,  right.baggageIncluded,  'bool',    3);
  field('transferIncluded', left.transferIncluded, right.transferIncluded, 'bool',    3);

  // ── Overall verdict: FAIL if any error or uncertain, else PASS ──────────
  const allSeverities = [
    ...travDetails.map(t => t.severity),
    ...Object.keys(FIELD_LABELS).map(k => diff[k]?.severity).filter(Boolean),
  ];
  diff.verdict = allSeverities.some(s => s === 'error' || s === 'uncertain') ? 'FAIL' : 'PASS';

  return diff;
}

/* ── Export ────────────────────────────────────────────────────────────── */

const FIELD_LABELS = {
  destination: 'Bestemming',
  hotel: 'Hotel / Accommodatie',
  departureDate: 'Vertrekdatum',
  returnDate: 'Terugkeerdatum',
  duration: 'Reisduur (dagen)',
  outboundFlight: 'Vluchtnummer heen',
  outboundDep: 'Vertrektijd heen',
  outboundArr: 'Aankomsttijd heen',
  returnFlight: 'Vluchtnummer terug',
  returnDep: 'Vertrektijd terug',
  returnArr: 'Aankomsttijd terug',
  boardType: 'Verzorging',
  roomType: 'Kamertype',
  totalPrice: 'Totaalprijs (€)',
  baggageIncluded: 'Ruimbagage inbegrepen',
  transferIncluded: 'Transfer inbegrepen',
};

const SEV_LABEL = { error: '[FOUT]', uncertain: '[ONZEKER]', warning: '[WAARSCH.]', correct: '' };

function downloadReport(diff, leftParsed, rightParsed, agentName, operatorLabel) {
  const doc = new jsPDF();
  const now = new Date();
  const isPass = diff.verdict === 'PASS';

  doc.setFontSize(16);
  doc.setFont(undefined, 'bold');
  doc.text('Afwijkingsrapport Boekingsverificatie', 14, 18);

  // Verdict banner
  doc.setFontSize(13);
  doc.setFont(undefined, 'bold');
  if (isPass) {
    doc.setTextColor(0, 140, 0);
    doc.text('UITKOMST: PASS', 14, 27);
  } else {
    doc.setTextColor(200, 0, 0);
    doc.text('UITKOMST: FAIL', 14, 27);
  }

  doc.setFontSize(9);
  doc.setFont(undefined, 'normal');
  doc.setTextColor(100);
  doc.text(`Controle uitgevoerd op: ${now.toLocaleString('nl-NL')}`, 14, 34);
  if (agentName) doc.text(`Agent: ${agentName}`, 14, 39);
  if (leftParsed?.reservationNumber) doc.text(`Reserveringsnummer BAS: ${leftParsed.reservationNumber}`, 14, 44);
  if (rightParsed?.reservationNumber) doc.text(`Reserveringsnummer ${operatorLabel}: ${rightParsed.reservationNumber}`, 14, 49);

  doc.setDrawColor(200);
  doc.line(14, 54, 196, 54);

  doc.setTextColor(0);
  let y = 62;

  // Traveler deviations
  if (diff.travelers?.details) {
    for (const t of diff.travelers.details) {
      if (t.severity === 'correct') continue;
      const prefix = SEV_LABEL[t.severity] || '';
      doc.setFontSize(10);
      doc.setFont(undefined, 'bold');
      doc.text(`Reiziger  ${prefix}`, 14, y);
      doc.setFont(undefined, 'normal');
      const nameL = t.left  ? `${t.left.firstName}  ${t.left.lastName}`  : '—';
      const nameR = t.right ? `${t.right.firstName} ${t.right.lastName}` : '—';
      doc.text(`BAS: ${nameL}`, 60, y);
      doc.text(`${operatorLabel}: ${nameR}`, 130, y);
      if (t.detail) {
        y += 5;
        doc.setFontSize(8);
        doc.setTextColor(120);
        doc.text(t.detail, 60, y, { maxWidth: 130 });
        doc.setTextColor(0);
        doc.setFontSize(10);
      }
      y += 8;
      if (y > 270) { doc.addPage(); y = 20; }
    }
  }

  // Field deviations
  for (const [key, label] of Object.entries(FIELD_LABELS)) {
    const entry = diff[key];
    if (!entry || entry.severity === 'correct') continue;
    const prefix = SEV_LABEL[entry.severity] || '';
    doc.setFontSize(10);
    doc.setFont(undefined, 'bold');
    doc.text(`${label}  ${prefix}`, 14, y);
    doc.setFont(undefined, 'normal');
    const lv = entry.left  != null ? String(entry.left)  : 'Niet beschikbaar';
    const rv = entry.right != null ? String(entry.right) : 'Niet beschikbaar';
    doc.text(`BAS: ${lv}`, 60, y);
    doc.text(`${operatorLabel}: ${rv}`, 130, y);
    if (entry.detail) {
      y += 5;
      doc.setFontSize(8);
      doc.setTextColor(120);
      doc.text(entry.detail, 60, y, { maxWidth: 130 });
      doc.setTextColor(0);
      doc.setFontSize(10);
    }
    y += 8;
    if (y > 270) { doc.addPage(); y = 20; }
  }

  const hasDeviations = [
    ...(diff.travelers?.details || []).filter(t => t.severity !== 'correct'),
    ...Object.keys(FIELD_LABELS).filter(k => diff[k] && diff[k].severity !== 'correct'),
  ].length > 0;

  if (!hasDeviations) {
    doc.setFontSize(10);
    doc.setTextColor(0, 150, 0);
    doc.text('Geen afwijkingen gevonden — bevestigingen komen overeen.', 14, y);
  }

  doc.save(`afwijkingsrapport_${leftParsed?.reservationNumber || 'boeking'}_${now.toISOString().split('T')[0]}.pdf`);
}

/* ── DropZone ──────────────────────────────────────────────────────────── */

function DropZone({ label, accept, file, loading, error, parsed, onFile, accentColor }) {
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef(null);

  const handleDrop = useCallback(e => {
    e.preventDefault();
    setDragging(false);
    const f = e.dataTransfer.files[0];
    if (f) onFile(f);
  }, [onFile]);

  const accent = accentColor === 'purple'
    ? { border: 'border-purple-300', bg: 'bg-purple-50', text: 'text-purple-700', light: 'bg-purple-50' }
    : { border: 'border-blue-300', bg: 'bg-blue-50', text: 'text-blue-700', light: 'bg-blue-50' };

  return (
    <div
      className={`flex-1 border border-dashed rounded-lg p-5 flex flex-col items-center justify-center gap-3 cursor-pointer transition-all ${
        dragging ? `${accent.border} ${accent.bg}` : 'border-gray-300 hover:border-gray-400 bg-gray-50'
      } ${error ? 'border-red-300 bg-red-50' : ''}`}
      onClick={() => inputRef.current?.click()}
      onDragOver={e => { e.preventDefault(); setDragging(true); }}
      onDragLeave={() => setDragging(false)}
      onDrop={handleDrop}
    >
      <input ref={inputRef} type="file" accept={accept} className="hidden"
        onChange={e => { if (e.target.files[0]) onFile(e.target.files[0]); }} />

      {loading ? (
        <>
          <span className="spinner" style={{ width: 28, height: 28, borderWidth: 3 }} />
          <p className="text-sm text-gray-500">Bestand verwerken…</p>
        </>
      ) : file ? (
        <div className="w-full text-center space-y-1.5">
          <p className="text-sm font-medium text-gray-700 truncate">{file.name}</p>
          <p className="text-xs text-gray-400">{(file.size / 1024).toFixed(0)} KB</p>
          {error
            ? <p className="text-xs text-red-600 mt-1">{error}</p>
            : parsed
              ? <p className="text-xs text-green-600 font-medium">✓ Ingelezen als {parsed._format}</p>
              : null}
          <button onClick={e => { e.stopPropagation(); onFile(null); }}
            className="text-xs text-gray-400 hover:text-red-500 underline mt-1">Verwijder</button>
        </div>
      ) : (
        <>
          <svg className="w-8 h-8 text-gray-300 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 13h6m-3-3v6m5 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          <p className="text-sm font-semibold text-gray-600 text-center">{label}</p>
          <p className="text-xs text-gray-400 text-center">Sleep een bestand hierheen of klik om te bladeren</p>
          <p className="text-xs text-gray-300">{accept.replace(/\./g, '').toUpperCase()}</p>
        </>
      )}
    </div>
  );
}

/* ── Main page ─────────────────────────────────────────────────────────── */

/* ── Email content extractor ──────────────────────────────────────────── */

function decodeQuotedPrintable(str) {
  return str.replace(/=\r?\n/g, '').replace(/=([0-9A-Fa-f]{2})/g, (_, h) => String.fromCharCode(parseInt(h, 16)));
}

function htmlToText(html) {
  try {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    function walk(node) {
      if (node.nodeType === 3) return node.textContent.replace(/[ \t]+/g, ' ');
      if (node.nodeType !== 1) return '';
      const tag = node.tagName.toLowerCase();
      if (tag === 'script' || tag === 'style') return '';
      const inner = Array.from(node.childNodes).map(walk).join('');
      if (tag === 'br') return '\n';
      if (tag === 'td' || tag === 'th') return inner.trim() + '\t';
      if (tag === 'tr') return inner.replace(/\t+$/, '').replace(/\t/g, ' ') + '\n';
      if (/^(p|div|li|h[1-6]|section|article|header|footer)$/.test(tag)) return inner.trim() + '\n';
      return inner;
    }
    return walk(doc.body)
      .replace(/[ \t]+/g, ' ')
      .replace(/\n[ \t]+/g, '\n')
      .replace(/[ \t]+\n/g, '\n')
      .replace(/\n{3,}/g, '\n\n')
      .trim();
  } catch {
    return html.replace(/<[^>]+>/g, ' ');
  }
}

async function extractTextFromEmailFile(file) {
  const raw = await file.text();
  const ext = file.name.split('.').pop().toLowerCase();

  if (ext === 'html' || ext === 'htm') {
    return htmlToText(raw);
  }

  // .eml — parse MIME
  const boundaryM = raw.match(/boundary=(?:"([^"]+)"|([^\s;]+))/i);
  const boundary = boundaryM?.[1] || boundaryM?.[2];

  if (boundary) {
    const parts = raw.split(new RegExp(`--${boundary.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}`, 'g'));
    for (const part of parts) {
      if (!/Content-Type:\s*text\/html/i.test(part)) continue;
      const bodyStart = part.indexOf('\n\n');
      if (bodyStart === -1) continue;
      let body = part.slice(bodyStart + 2);
      if (/Content-Transfer-Encoding:\s*base64/i.test(part)) {
        try { body = atob(body.replace(/\s/g, '')); } catch { /* keep raw */ }
      } else if (/Content-Transfer-Encoding:\s*quoted-printable/i.test(part)) {
        body = decodeQuotedPrintable(body);
      }
      const text = htmlToText(body);
      if (text.trim().length > 50) return text;
    }
  }

  // No MIME boundary — try raw body after headers
  const headerEnd = raw.indexOf('\n\n');
  const body = headerEnd >= 0 ? raw.slice(headerEnd + 2) : raw;
  if (/<html/i.test(body)) return htmlToText(body);
  return body;
}

/* ── Corendon email text parser ────────────────────────────────────────── */

// Splits a Dutch full name into firstName + lastName, handling tussenvoegsels
// e.g. "Trynke de Haan" → {firstName:"Trynke", lastName:"de Haan"}
// e.g. "Ineke van der Veen" → {firstName:"Ineke", lastName:"van der Veen"}
function splitDutchName(name) {
  const words = name.trim().split(/\s+/);
  if (words.length <= 1) return { firstName: words[0] || '', lastName: words[0] || '' };
  // Tussenvoegsel = first lowercase-starting word after index 0 marks start of surname
  const tvIdx = words.findIndex((w, i) => i > 0 && /^[a-z]/.test(w));
  if (tvIdx > 0) {
    return {
      firstName: words.slice(0, tvIdx).join(' '),
      lastName:  words.slice(tvIdx).join(' '),
    };
  }
  // No tussenvoegsel: all but last = firstName, last = lastName
  return {
    firstName: words.slice(0, -1).join(' '),
    lastName:  words[words.length - 1],
  };
}

function parseCorendonEmail(text) {
  if (!text || !/Boekingsnummer|Corendon/i.test(text)) return null;

  const result = {
    travelers: [], destination: null, hotel: null,
    departureDate: null, returnDate: null, duration: null,
    outboundFlight: null, outboundDep: null, outboundArr: null,
    returnFlight: null, returnDep: null, returnArr: null,
    boardType: null, roomType: null, totalPrice: null,
    baggageIncluded: false, transferIncluded: false,
    reservationNumber: null, pnr: null, _format: 'Corendon',
  };

  // ── Booking number ─────────────────────────────────────────────────────
  // "Boekingsnummer 2777355" (same-line after htmlToText) or next-line fallback
  const bnM = text.match(/Boekingsnummer\s+(\d{5,10})/i)
           || text.match(/Boekingsnummer\s*\n+\s*(\d{5,10})/i);
  if (bnM) result.reservationNumber = bnM[1];

  // ── Hotel ──────────────────────────────────────────────────────────────
  const hotelM = text.match(/Accommodatie\s+([^\n]+)/i)
              || text.match(/Accommodatie\s*\n+([^\n]+)/i);
  if (hotelM) result.hotel = hotelM[1].trim().replace(/\s+$/, '');

  // ── Destination (most specific part of Locatie) ────────────────────────
  // "Locatie Portugal / Madeira / Funchal" → use last segment "Funchal"
  const locM = text.match(/Locatie\s+([^\n]+)/i)
             || text.match(/Locatie\s*\n+([^\n]+)/i);
  if (locM) {
    const parts = locM[1].split('/').map(p => p.trim()).filter(Boolean);
    result.destination = parts[parts.length - 1] || parts[0];
  }

  // ── Year context ───────────────────────────────────────────────────────
  const yearM = text.match(/\b(20\d{2})\b/);
  const year = yearM ? parseInt(yearM[1]) : new Date().getFullYear();

  const MONTHS_NL = { jan:1,feb:2,mrt:3,apr:4,mei:5,jun:6,jul:7,aug:8,sep:9,okt:10,nov:11,dec:12 };
  function parseDutchDate(str) {
    const m = str.match(/(\d{1,2})\s+(\w{3})/);
    if (!m) return null;
    const d = parseInt(m[1]), mo = MONTHS_NL[m[2].toLowerCase()];
    if (!d || !mo) return null;
    return `${year}-${String(mo).padStart(2,'0')}-${String(d).padStart(2,'0')}`;
  }

  // ── Check-in / Check-out ───────────────────────────────────────────────
  // "Inchecken zondag 13 sep" or "Inchecken\nzondag 13 sep"
  const inM  = text.match(/Inchecken\s+(?:\w+\s+)?(\d{1,2}\s+\w{3})/i)
            || text.match(/Inchecken\s*\n+(?:\w+\s+)?(\d{1,2}\s+\w{3})/i);
  const outM = text.match(/Uitchecken\s+(?:\w+\s+)?(\d{1,2}\s+\w{3})/i)
            || text.match(/Uitchecken\s*\n+(?:\w+\s+)?(\d{1,2}\s+\w{3})/i);
  if (inM) result.departureDate = parseDutchDate(inM[1]);
  if (outM) {
    let ret = parseDutchDate(outM[1]);
    // Handle year rollover (e.g. Dec → Jan)
    if (ret && result.departureDate) {
      const depMo = parseInt(result.departureDate.slice(5, 7));
      const retMo = parseInt(ret.slice(5, 7));
      if (retMo < depMo) ret = `${year + 1}${ret.slice(4)}`;
    }
    result.returnDate = ret;
  }
  if (result.departureDate && result.returnDate) {
    const days = (new Date(result.returnDate) - new Date(result.departureDate)) / 86400000;
    result.duration = Math.round(days) + 1;
  }

  // ── Room type + board type ─────────────────────────────────────────────
  // "Kamer en verzorging\nStudio Standaard Zijzeezicht Logies"
  // Room type is before the board type keyword at end of line
  const kamerM = text.match(/Kamer en verzorging[^\n]*\n([^\n]+)/i);
  if (kamerM) {
    const line = kamerM[1].trim();
    const boardRe = /\s+(Logies|All[\s\-]*[Ii]nclusive|Allinclusief|Half[\s\-]*[Pp]ension|Vol[\s\-]*[Pp]ension|Bed\s*[&en]+\s*Breakfast|AI|HP|VP|BB|FB)\s*$/i;
    const bm = line.match(boardRe);
    if (bm) {
      result.boardType = normalizeBoardType(bm[1]);
      result.roomType = line.slice(0, bm.index).trim();
    } else {
      result.roomType = line;
    }
  }
  // Fallback board type: search anywhere in text
  if (!result.boardType) {
    const anyB = text.match(/\b(Logies|All[\s\-]*[Ii]nclusive|Half[\s\-]*[Pp]ension|Vol[\s\-]*[Pp]ension)\b/);
    if (anyB) result.boardType = normalizeBoardType(anyB[1]);
  }

  // ── Flights ────────────────────────────────────────────────────────────
  // "Heenvlucht HV 6631 (Directe vlucht)" — flight number on same line as heading
  const heenFn = text.match(/Heenvlucht\s+([A-Z]{2}\s*\d{3,4})/i)
              || text.match(/Heenvlucht[^\n]*\n\s*([A-Z]{2}\s*\d{3,4})/i);
  if (heenFn) result.outboundFlight = heenFn[1].replace(/\s+/, ' ').trim();

  const heenSection = text.match(/Heenvlucht([\s\S]*?)(?=Terugvlucht|Bagage|$)/i);
  if (heenSection) {
    const times = [...heenSection[1].matchAll(/[\-–]\s*(\d{2}:\d{2})\b/g)];
    if (times[0]) result.outboundDep = times[0][1];
    if (times[1]) result.outboundArr = times[1][1];
    // Departure date from heenvlucht line (e.g. "zondag 13 sep - 14:40 Amsterdam")
    if (!result.departureDate) {
      const dM = heenSection[1].match(/(\d{1,2}\s+\w{3})\s*[\-–]/);
      if (dM) result.departureDate = parseDutchDate(dM[1]);
    }
  }

  const terugFn = text.match(/Terugvlucht\s+([A-Z]{2}\s*\d{3,4})/i)
               || text.match(/Terugvlucht[^\n]*\n\s*([A-Z]{2}\s*\d{3,4})/i);
  if (terugFn) result.returnFlight = terugFn[1].replace(/\s+/, ' ').trim();

  const terugSection = text.match(/Terugvlucht([\s\S]*?)(?=Bagage|Reiziger|Totaal|Geboekte|$)/i);
  if (terugSection) {
    const times = [...terugSection[1].matchAll(/[\-–]\s*(\d{2}:\d{2})\b/g)];
    if (times[0]) result.returnDep = times[0][1];
    if (times[1]) result.returnArr = times[1][1];
  }

  // ── Travelers ──────────────────────────────────────────────────────────
  // The Reizigers table has 3 columns: salutation | full name | DOB
  // After htmlToText (cells joined with space per row):
  //   "Mevr. Trynke de Haan 01-02-1984"
  //   "Mevr. Ineke van der Veen 10-03-1979"
  // Regex: line starts with salutation, ends with dd-mm-yyyy
  const reizSection = text.match(/Reizigers?\s*\n([\s\S]*?)(?=\n(?:Overzicht|Kamer|Vlucht|Bagage|Geboekte|Prijsberekening|Hoofdboeker)|\n{3}|$)/i);
  const searchArea = reizSection ? reizSection[1] : text;

  const travRe = /^(?:Mevr\.?|Dhr\.?|Mstr\.?|De heer)\s+(.+?)\s+(\d{2}-\d{2}-\d{4})\s*$/gim;
  let tm;
  while ((tm = travRe.exec(searchArea)) !== null) {
    const { firstName, lastName } = splitDutchName(tm[1].trim());
    result.travelers.push({ firstName, lastName, dob: normalizeDate(tm[2]) });
  }

  // De-duplicate travelers (header section may also match)
  const seen = new Set();
  result.travelers = result.travelers.filter(t => {
    const key = `${normSurname(t.lastName)}_${t.dob}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });

  // ── Price ──────────────────────────────────────────────────────────────
  // "Totaalprijs €1765,50" (same line) or next line
  const priceM = text.match(/Totaalprijs\s+€?\s*([\d.,]+)/i)
              || text.match(/Totaalprijs[^\n]*\n\s*€?\s*([\d.,]+)/i);
  if (priceM) result.totalPrice = parsePrice(priceM[1]);

  // ── Baggage ────────────────────────────────────────────────────────────
  result.baggageIncluded = /\b20\s*kg\b|\bruimbagage\b/i.test(text);

  // ── Transfer ───────────────────────────────────────────────────────────
  // "Bustransfer niet geboekt" = NOT included
  result.transferIncluded = /\b(?:bus)?transfer\b/i.test(text)
    && !/bustransfer\s+niet\s+geboekt|geen\s*(?:bus)?transfer/i.test(text);

  return result;
}

/* ── Client-side BAS XML builder ──────────────────────────────────────── */

function buildBASXml(booking, basNumber) {
  const now = new Date();
  const dateStr = now.toISOString().split('T')[0];
  const timeStr = now.toTimeString().slice(0, 5);
  const receiverID = String(basNumber).padStart(5, '0');
  const sessionID = `CORNL${booking.reservationNumber || '000000'}${Date.now()}`;

  const esc = s => String(s ?? '')
    .replace(/&/g, '&amp;').replace(/</g, '&lt;')
    .replace(/>/g, '&gt;').replace(/"/g, '&quot;');

  // Board type code
  const btMap = { 'All Inclusive': 'AI', 'Logies': 'LO', 'Half Pension': 'HP', 'Vol Pension': 'VP', 'Bed & Breakfast': 'BB' };
  const btCode = btMap[booking.boardType] || 'LO';

  // Price in eurocents
  const totalCents = Math.round((typeof booking.totalPrice === 'number' ? booking.totalPrice : parseFloat(String(booking.totalPrice || 0).replace(',', '.')) || 0) * 100);

  const travelers = booking.travelers || [];
  const adults = travelers.length;

  const paxBlocks = travelers.map((t, i) => `
      <PaxDescription>
        <PaxID>${i + 1}</PaxID>
        <Initials>${esc((t.firstName || '')[0] || '?')}</Initials>
        <FirstName>${esc(t.firstName)}</FirstName>
        <Name>${esc(t.lastName)}</Name>
        <DateOfBirth>${esc(t.dob)}</DateOfBirth>
        <Gender>V</Gender>
        <Nationality>NL</Nationality>
      </PaxDescription>`).join('');

  const baggageBlock = booking.baggageIncluded ? `
      <Extras choice="optional">
        <ExtraID>BAG020</ExtraID>
        <Description>Ruimbagage 20 kg</Description>
        <From><Date>${esc(booking.departureDate)}</Date></From>
        <To><Date>${esc(booking.returnDate)}</Date></To>
        <Allotment>${adults}</Allotment>
        <Status>Akkoord</Status>
      </Extras>` : '';

  return `<?xml version="1.0" encoding="utf-8"?>
<TravelMessage VersionID="1.7.3">
  <Control Language="NL">
    <SenderSessionID>${esc(sessionID)}</SenderSessionID>
    <ReceiverSessionID>${esc(sessionID)}</ReceiverSessionID>
    <Date>${dateStr}</Date>
    <Time>${timeStr}</Time>
    <MessageSequence>1</MessageSequence>
    <SenderID>CORNL</SenderID>
    <ReceiverID>${receiverID}</ReceiverID>
    <EmployeeID>System</EmployeeID>
    <RequestID>RecapRequest</RequestID>
    <ResponseID>RecapResponse</ResponseID>
    <ResponseStatus>ja</ResponseStatus>
  </Control>
  <TResponse>
    <PackageDetails>
      <PackageID>${esc(booking.reservationNumber)}</PackageID>
      <NumberOfAdults>${adults}</NumberOfAdults>
      <NumberOfChildren>0</NumberOfChildren>
      <NumberOfBabies>0</NumberOfBabies>
      <DepartureDate>${esc(booking.departureDate)}</DepartureDate>
      <Duration>${booking.duration ?? ''}</Duration>
    </PackageDetails>
    <Transport>
      <TransportTo>
        <TransportID>${esc(booking.outboundFlight ?? '')}</TransportID>
        <TransportType>VL</TransportType>
        <Carrier>
          <CarrierID>HV</CarrierID>
          <Description>Corendon Airlines</Description>
          <FlightNumber>${esc(booking.outboundFlight ?? '')}</FlightNumber>
        </Carrier>
        <Departure>
          <Point PointType="Name"><PointID>Amsterdam Schiphol</PointID></Point>
          <Date>${esc(booking.departureDate)}</Date>
          <Time>${esc(booking.outboundDep ?? '')}</Time>
        </Departure>
        <Arrival>
          <Point PointType="Name"><PointID>${esc(booking.destination ?? '')}</PointID></Point>
          <Date>${esc(booking.departureDate)}</Date>
          <Time>${esc(booking.outboundArr ?? '')}</Time>
        </Arrival>
        <Status>Akkoord</Status>
        <Class><ClassID>E</ClassID><Description>Economy</Description></Class>
      </TransportTo>
      <TransportFrom>
        <TransportID>${esc(booking.returnFlight ?? '')}</TransportID>
        <TransportType>VL</TransportType>
        <Carrier>
          <CarrierID>HV</CarrierID>
          <Description>Corendon Airlines</Description>
          <FlightNumber>${esc(booking.returnFlight ?? '')}</FlightNumber>
        </Carrier>
        <Departure>
          <Point PointType="Name"><PointID>${esc(booking.destination ?? '')}</PointID></Point>
          <Date>${esc(booking.returnDate)}</Date>
          <Time>${esc(booking.returnDep ?? '')}</Time>
        </Departure>
        <Arrival>
          <Point PointType="Name"><PointID>Amsterdam Schiphol</PointID></Point>
          <Date>${esc(booking.returnDate)}</Date>
          <Time>${esc(booking.returnArr ?? '')}</Time>
        </Arrival>
        <Status>Akkoord</Status>
        <Class><ClassID>E</ClassID><Description>Economy</Description></Class>
      </TransportFrom>
    </Transport>
    <Accommodation>
      <AccommodationID>${esc(booking.reservationNumber)}</AccommodationID>
      <AccommodationType>HOT</AccommodationType>
      <Name>${esc(booking.hotel)}</Name>
      <Place>${esc(booking.destination)}</Place>
      <Country></Country>
      <ArrivalDate>${esc(booking.departureDate)}</ArrivalDate>
      <DepartureDate>${esc(booking.returnDate)}</DepartureDate>
      <Unit>
        <UnitType>STD</UnitType>
        <Description>${esc(booking.roomType)}</Description>
        <Board>
          <BoardType>${btCode}</BoardType>
          <Description>${esc(booking.boardType)}</Description>
        </Board>
        <Allotment>1</Allotment>
        <Status>Akkoord</Status>
      </Unit>
    </Accommodation>
    <AdditionalProducts>${baggageBlock}
    </AdditionalProducts>
    <PaxDetails>${paxBlocks}
      <Contact>
        <PaxID>00</PaxID>
        <Name>${esc(travelers[0]?.lastName ?? '')}</Name>
        <Country>NL</Country>
        <TelHome></TelHome><TelMobile></TelMobile><Email></Email>
      </Contact>
    </PaxDetails>
    <PriceInfo>
      <Price Currency="EUR" PricePer="PA">${totalCents}</Price>
    </PriceInfo>
    <BookResponse>
      <BookID>${esc(booking.reservationNumber)}</BookID>
      <Date>${dateStr}</Date>
      <Time>${timeStr}</Time>
      <Status>Akkoord</Status>
    </BookResponse>
  </TResponse>
</TravelMessage>`;
}

/* ── Source drawer field list ───────────────────────────────────────────── */

function DrawerFieldList({ parsed }) {
  if (!parsed) return <p className="text-xs text-gray-400 italic">Geen data</p>;
  const items = [
    ['Reizigers',      parsed.travelers?.map(t => `${t.firstName} ${t.lastName}${t.dob ? ` (${t.dob})` : ''}`).join(' · ') || null],
    ['Reserveringsnr.', parsed.reservationNumber],
    ['PNR',            parsed.pnr],
    ['Hotel',          parsed.hotel],
    ['Bestemming',     parsed.destination],
    ['Vertrekdatum',   parsed.departureDate],
    ['Terugkeerdatum', parsed.returnDate],
    ['Reisduur',       parsed.duration ? `${parsed.duration} dagen` : null],
    ['Heen',           parsed.outboundFlight ? `${parsed.outboundFlight}  ${parsed.outboundDep || ''}–${parsed.outboundArr || ''}` : null],
    ['Terug',          parsed.returnFlight   ? `${parsed.returnFlight}  ${parsed.returnDep || ''}–${parsed.returnArr || ''}`   : null],
    ['Kamer',          parsed.roomType],
    ['Verzorging',     parsed.boardType],
    ['Prijs',          parsed.totalPrice != null ? `€\u202f${Number(parsed.totalPrice).toLocaleString('nl-NL', { minimumFractionDigits: 2 })}` : null],
    ['Ruimbagage',     parsed.baggageIncluded != null ? (parsed.baggageIncluded ? 'Ja' : 'Nee') : null],
    ['Transfer',       parsed.transferIncluded != null ? (parsed.transferIncluded ? 'Ja' : 'Nee') : null],
  ];
  return (
    <dl className="space-y-2">
      {items.map(([key, val]) => val ? (
        <div key={key} className="flex gap-2">
          <dt className="text-gray-400 w-28 shrink-0 text-xs pt-0.5">{key}</dt>
          <dd className="text-gray-800 text-xs break-words leading-relaxed">{val}</dd>
        </div>
      ) : null)}
    </dl>
  );
}

const OPERATORS = [
  { value: 'corendon', label: 'Corendon', accept: '.pdf,.eml,.html,.htm' },
  { value: 'tui',      label: 'TUI',      accept: '.xml,.txt' },
  { value: 'sunweb',   label: 'Sunweb',   accept: '.pdf,.eml,.html,.htm' },
  { value: 'overig',   label: 'Overig',   accept: '.pdf,.xml,.txt,.eml,.html' },
];

function ReisbescheidenInner() {
  const { user, authFetch } = useAuth();
  const [activeTab, setActiveTab] = useState('vergelijken'); // 'vergelijken' | 'naar-bas'

  // ── Vergelijken tab state ──────────────────────────────────────────────
  const [operator, setOperator] = useState('corendon');
  const [leftFile, setLeftFile] = useState(null);
  const [rightFile, setRightFile] = useState(null);
  const [leftParsed, setLeftParsed] = useState(null);
  const [rightParsed, setRightParsed] = useState(null);
  const [loadingL, setLoadingL] = useState(false);
  const [loadingR, setLoadingR] = useState(false);
  const [errorL, setErrorL] = useState('');
  const [errorR, setErrorR] = useState('');
  const [diffResult, setDiffResult] = useState(null);
  const [rightEmailMeta, setRightEmailMeta] = useState(null);  // email metadata when right side is EML
  const [convertEmailMeta, setConvertEmailMeta] = useState(null); // email metadata for conversion tab

  // ── E-mail tab state ───────────────────────────────────────────────────
  const [emailLeftFile, setEmailLeftFile] = useState(null);
  const [emailLeftParsed, setEmailLeftParsed] = useState(null);
  const [emailLoadingL, setEmailLoadingL] = useState(false);
  const [emailErrorL, setEmailErrorL] = useState('');
  const [inboxEmails, setInboxEmails] = useState([]);
  const [inboxLoading, setInboxLoading] = useState(false);
  const [inboxError, setInboxError] = useState('');
  const [selectedEmailUid, setSelectedEmailUid] = useState(null);
  const [selectedEmailParsed, setSelectedEmailParsed] = useState(null);
  const [selectedEmailMeta, setSelectedEmailMeta] = useState(null);
  const [selectedEmailLoading, setSelectedEmailLoading] = useState(false);
  const [emailDiffResult, setEmailDiffResult] = useState(null);
  const [emailDetailsOpen, setEmailDetailsOpen] = useState(false);

  // ── Naar BAS tab state ─────────────────────────────────────────────────
  const [convertFile, setConvertFile] = useState(null);
  const [convertParsed, setConvertParsed] = useState(null);
  const [convertLoading, setConvertLoading] = useState(false);
  const [convertError, setConvertError] = useState('');
  const [generatedXml, setGeneratedXml] = useState('');
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  const [basNumber, setBasNumber] = useState('');
  const [imapUser, setImapUser] = useState('');
  const [sendToEmail, setSendToEmail] = useState('');
  const [reisbescheidenFolder, setReisbescheidenFolder] = useState('Reisbescheiden');
  const convertInputRef = useRef(null);

  // ── Validation result UX state ─────────────────────────────────────────
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [reviewedFields, setReviewedFields] = useState(new Set());
  const [confirmedWarnings, setConfirmedWarnings] = useState(new Set());
  const [comparing, setComparing] = useState(false);
  const [emailComparing, setEmailComparing] = useState(false);
  const [emailConfirmedWarnings, setEmailConfirmedWarnings] = useState(new Set());

  const toggleReviewed = id => setReviewedFields(prev => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  // Load BAS number + imap_user + folder from settings
  useEffect(() => {
    authFetch('/api/settings')
      .then(r => r.json())
      .then(d => {
        if (d.bas_number)            setBasNumber(d.bas_number);
        if (d.imap_user)             setImapUser(d.imap_user);
        if (d.reisbescheiden_folder) setReisbescheidenFolder(d.reisbescheiden_folder);
      })
      .catch(() => {});
  }, [authFetch]);

  // Load inbox emails when the email tab becomes active
  useEffect(() => {
    if (activeTab === 'email' && inboxEmails.length === 0) {
      fetchInboxEmails();
    }
  }, [activeTab]); // eslint-disable-line

  const handleConvertFile = useCallback(async file => {
    setConvertFile(file);
    setConvertParsed(null);
    setConvertEmailMeta(null);
    setConvertError('');
    setGeneratedXml('');
    setSendResult(null);
    if (!file) return;
    setConvertLoading(true);
    try {
      const ext = file.name.split('.').pop().toLowerCase();
      let parsed = null;

      if (ext === 'eml') {
        // Send to backend — handles PDF attachments inside EML correctly
        const token = localStorage.getItem('tripdash_token');
        const fd = new FormData();
        fd.append('file', file);
        const res = await fetch('/api/reisbescheiden/parse-eml', {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: fd,
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || 'EML verwerking mislukt');
        parsed = data.booking;
        setConvertEmailMeta(data.emailMeta);
      } else if (ext === 'html' || ext === 'htm') {
        const text = await extractTextFromEmailFile(file);
        parsed = parseCorendonEmail(text);
        if (!parsed) throw new Error('Geen Corendon-bevestiging herkend in dit e-mailbestand.');
      } else {
        const text = await extractPDFText(file);
        parsed = parseCorendon(text) || parseCorendonEmail(text);
        if (!parsed) throw new Error('Geen Corendon-bevestiging herkend in dit bestand.');
      }
      setConvertParsed(parsed);
    } catch (err) {
      setConvertError(err.message);
    }
    setConvertLoading(false);
  }, []);

  // ── Client-side XML generation (no API call — data never leaves browser) ──
  const handleGenerateXml = () => {
    if (!convertParsed) return;
    const xml = buildBASXml(convertParsed, basNumber || '00000');
    setGeneratedXml(xml);
    setSendResult(null);
  };

  const handleDownloadXml = () => {
    if (!generatedXml) return;
    const blob = new Blob([generatedXml], { type: 'application/xml' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `corendon_${convertParsed?.reservationNumber || 'boeking'}_${new Date().toISOString().split('T')[0]}.xml`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleSendEmail = async () => {
    if (!generatedXml || !sendToEmail.trim()) return;
    setSending(true);
    setSendResult(null);
    try {
      const res = await authFetch('/api/reisbescheiden/send-to-bas', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          xml: generatedXml,
          reservationNumber: convertParsed?.reservationNumber,
          toEmail: sendToEmail.trim(),
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Versturen mislukt');
      setSendResult({ ok: true, msg: `✓ Verstuurd naar ${data.sentTo}` });
    } catch (err) {
      setSendResult({ ok: false, msg: err.message });
    }
    setSending(false);
  };

  const handleSyncImap = async () => {
    setSyncing(true);
    setSyncResult(null);
    try {
      const res = await authFetch('/api/reisbescheiden/sync-imap', { method: 'POST' });
      const data = await res.json();
      setSyncResult({ ok: res.ok, msg: data.message || data.error, details: data.errors });
    } catch (err) {
      setSyncResult({ ok: false, msg: err.message });
    }
    setSyncing(false);
  };

  const opConfig = OPERATORS.find(o => o.value === operator);

  const handleLeftFile = useCallback(async file => {
    setLeftFile(file);
    setLeftParsed(null);
    setErrorL('');
    setDiffResult(null);
    if (!file) return;
    setLoadingL(true);
    try {
      const text = await extractPDFText(file);
      const parsed = parseBAS(text);
      if (!parsed) throw new Error('Geen BAS-bevestiging herkend. Controleer of dit het juiste bestand is.');
      setLeftParsed(parsed);
    } catch (err) {
      setErrorL(err.message || 'Fout bij verwerken PDF');
    }
    setLoadingL(false);
  }, []);

  const handleRightFile = useCallback(async file => {
    setRightFile(file);
    setRightParsed(null);
    setRightEmailMeta(null);
    setErrorR('');
    setDiffResult(null);
    if (!file) return;
    setLoadingR(true);
    try {
      let parsed = null;
      const ext = file.name.split('.').pop().toLowerCase();

      if (ext === 'xml' || ext === 'txt') {
        const text = await file.text();
        parsed = parseTUIXML(text);
        if (!parsed) throw new Error('Geen geldige TUI RecapResponse XML herkend.');
      } else if (ext === 'eml') {
        const token = localStorage.getItem('tripdash_token');
        const fd = new FormData();
        fd.append('file', file);
        const res = await fetch('/api/reisbescheiden/parse-eml', {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: fd,
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || 'EML verwerking mislukt');
        parsed = data.booking;
        setRightEmailMeta(data.emailMeta);
      } else if (ext === 'html' || ext === 'htm') {
        const text = await extractTextFromEmailFile(file);
        parsed = parseCorendonEmail(text);
        if (!parsed) throw new Error('Geen bevestiging herkend in dit e-mailbestand.');
      } else {
        // PDF — try Corendon fixed-width invoice, then Gmail/email PDF, then BAS
        const text = await extractPDFText(file);
        parsed = parseCorendon(text) || parseCorendonEmail(text) || parseBAS(text);
        if (!parsed) throw new Error('Kon dit document niet verwerken als bekende boekingsbevestiging.');
      }
      setRightParsed(parsed);
    } catch (err) {
      setErrorR(err.message || 'Fout bij verwerken bestand');
    }
    setLoadingR(false);
  }, []);

  const applyAIClearing = (result, cleared) => {
    for (const key of cleared) {
      if (key.startsWith('field-')) {
        const fk = key.slice(6);
        if (result[fk]) result[fk] = { ...result[fk], severity: 'correct' };
      } else if (key.startsWith('traveler-')) {
        const idx = parseInt(key.slice(9));
        if (result.travelers?.details?.[idx]) {
          result.travelers.details[idx] = { ...result.travelers.details[idx], severity: 'correct' };
        }
      }
    }
    // Recalculate travelers overall severity
    if (result.travelers?.details) {
      const sevRank = s => ({ error: 3, uncertain: 2, warning: 1, correct: 0 }[s] ?? 0);
      result.travelers.severity = result.travelers.details.reduce(
        (acc, t) => sevRank(t.severity) > sevRank(acc) ? t.severity : acc, 'correct'
      );
    }
    // Recalculate verdict
    const allSevs = [
      ...Object.keys(FIELD_LABELS).map(k => result[k]?.severity).filter(Boolean),
      ...(result.travelers?.details || []).map(t => t.severity),
    ];
    result.verdict = allSevs.some(s => s === 'error' || s === 'uncertain') ? 'FAIL' : 'PASS';
  };

  const collectWarnings = (result) => {
    const warnings = [];
    for (const [key, label] of Object.entries(FIELD_LABELS)) {
      const entry = result[key];
      if (entry && entry.severity === 'warning') {
        warnings.push({ key: `field-${key}`, label, left: String(entry.left ?? ''), right: String(entry.right ?? '') });
      }
    }
    if (result.travelers?.details) {
      result.travelers.details.forEach((t, i) => {
        if (t.severity === 'warning') {
          warnings.push({
            key: `traveler-${i}`, label: 'Reiziger',
            left: t.left ? `${t.left.firstName} ${t.left.lastName}` : '',
            right: t.right ? `${t.right.firstName} ${t.right.lastName}` : '',
          });
        }
      });
    }
    return warnings;
  };

  const handleCompare = async () => {
    if (!leftParsed || !rightParsed || comparing) return;
    setComparing(true);
    const result = compareBookings(leftParsed, rightParsed);

    const warnings = collectWarnings(result);
    if (warnings.length > 0) {
      try {
        const res = await authFetch('/api/reisbescheiden/validate-warnings', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ warnings }),
        });
        const data = await res.json();
        if (Array.isArray(data.cleared) && data.cleared.length > 0) {
          applyAIClearing(result, data.cleared);
        }
      } catch (_) { /* non-fatal */ }
    }

    setDiffResult(result);
    setReviewedFields(new Set());
    setConfirmedWarnings(new Set());
    setDetailsOpen(result.verdict !== 'PASS');
    setComparing(false);
  };

  const handleReset = () => {
    setLeftFile(null); setRightFile(null);
    setLeftParsed(null); setRightParsed(null);
    setRightEmailMeta(null);
    setDiffResult(null);
    setErrorL(''); setErrorR('');
    setReviewedFields(new Set());
    setConfirmedWarnings(new Set());
    setDetailsOpen(false);
  };

  // ── E-mail tab handlers ────────────────────────────────────────────────

  const handleEmailLeftFile = useCallback(async file => {
    setEmailLeftFile(file);
    setEmailLeftParsed(null);
    setEmailErrorL('');
    setEmailDiffResult(null);
    if (!file) return;
    setEmailLoadingL(true);
    try {
      const text = await extractPDFText(file);
      const parsed = parseBAS(text);
      if (!parsed) throw new Error('Geen BAS-bevestiging herkend. Controleer of dit het juiste bestand is.');
      setEmailLeftParsed(parsed);
    } catch (err) {
      setEmailErrorL(err.message || 'Fout bij verwerken PDF');
    }
    setEmailLoadingL(false);
  }, []);

  const fetchInboxEmails = useCallback(async () => {
    setInboxLoading(true);
    setInboxError('');
    try {
      const res = await authFetch('/api/reisbescheiden/inbox-emails');
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Ophalen mislukt');
      setInboxEmails(data.emails || []);
    } catch (err) {
      setInboxError(err.message);
    }
    setInboxLoading(false);
  }, [authFetch]);

  const handleSelectEmail = useCallback(async (uid) => {
    if (selectedEmailUid === uid) return; // already selected
    setSelectedEmailUid(uid);
    setSelectedEmailParsed(null);
    setSelectedEmailMeta(null);
    setEmailDiffResult(null);
    setSelectedEmailLoading(true);
    try {
      const res = await authFetch('/api/reisbescheiden/inbox-email-parse', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ uid }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Verwerken mislukt');
      setSelectedEmailParsed(data.booking);
      setSelectedEmailMeta(data.emailMeta);
    } catch (err) {
      setInboxError(err.message);
      setSelectedEmailUid(null);
    }
    setSelectedEmailLoading(false);
  }, [authFetch, selectedEmailUid]);

  const handleEmailCompare = async () => {
    if (!emailLeftParsed || !selectedEmailParsed || emailComparing) return;
    setEmailComparing(true);
    const result = compareBookings(emailLeftParsed, selectedEmailParsed);

    const warnings = collectWarnings(result);
    if (warnings.length > 0) {
      try {
        const res = await authFetch('/api/reisbescheiden/validate-warnings', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ warnings }),
        });
        const data = await res.json();
        if (Array.isArray(data.cleared) && data.cleared.length > 0) {
          applyAIClearing(result, data.cleared);
        }
      } catch (_) { /* non-fatal */ }
    }

    setEmailDiffResult(result);
    setEmailConfirmedWarnings(new Set());
    setEmailDetailsOpen(result.verdict !== 'PASS');
    setEmailComparing(false);
  };

  const handleEmailBevestig = async () => {
    // Move email to history and reset email comparison state
    if (selectedEmailUid) {
      authFetch('/api/reisbescheiden/move-to-history', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ uid: selectedEmailUid }),
      }).catch(() => {});
    }
    setEmailDiffResult(null);
    setSelectedEmailParsed(null);
    setSelectedEmailUid(null);
    setSelectedEmailMeta(null);
    setEmailConfirmedWarnings(new Set());
    // Refresh inbox to reflect moved email
    fetchInboxEmails();
  };

  const canCompare = leftParsed && rightParsed && !loadingL && !loadingR && !comparing;

  // Summary counts
  let errorCount = 0, warningCount = 0, uncertainCount = 0, correctCount = 0;
  if (diffResult) {
    const countSev = s => {
      if (s === 'error') errorCount++;
      else if (s === 'uncertain') uncertainCount++;
      else if (s === 'warning') warningCount++;
      else correctCount++;
    };
    for (const key of Object.keys(FIELD_LABELS)) {
      if (diffResult[key]) countSev(diffResult[key].severity);
    }
    if (diffResult.travelers) countSev(diffResult.travelers.severity);
  }
  const isPass = diffResult?.verdict === 'PASS';
  const blockingCount = errorCount + uncertainCount;

  // Problem-first issues list
  const issues = [];
  if (diffResult) {
    for (const [i, t] of (diffResult.travelers?.details || []).entries()) {
      if (t.severity !== 'correct') {
        issues.push({
          id: `traveler-${i}`,
          severity: t.severity,
          label: 'Reiziger',
          description: t.detail || (t.left && t.right
            ? `${t.left.firstName} ${t.left.lastName} ≠ ${t.right.firstName} ${t.right.lastName}`
            : null),
        });
      }
    }
    for (const [key, label] of Object.entries(FIELD_LABELS)) {
      const entry = diffResult[key];
      if (entry && entry.severity !== 'correct') {
        issues.push({
          id: `field-${key}`,
          severity: entry.severity,
          label,
          description: entry.detail
            || (entry.left != null && entry.right != null
              ? `"${String(entry.left)}" ≠ "${String(entry.right)}"`
              : entry.left == null ? 'Ontbreekt in BAS' : 'Ontbreekt bij operator'),
        });
      }
    }
    const sevRank = { error: 0, uncertain: 1, warning: 2 };
    issues.sort((a, b) => (sevRank[a.severity] ?? 3) - (sevRank[b.severity] ?? 3));
  }

  const formatVal = (key, v) => {
    if (v == null) return <span className="text-gray-300 italic text-xs">Niet beschikbaar</span>;
    if (typeof v === 'boolean') return v ? '✓ Ja' : '✗ Nee';
    if (key === 'totalPrice') return `€\u202f${Number(v).toLocaleString('nl-NL', { minimumFractionDigits: 2 })}`;
    return String(v);
  };

  return (
    <div className="h-full overflow-y-auto bg-gray-50">
      {/* Header */}
      <div className="pro-page-header flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">Reisbescheiden</h1>
          <p className="text-sm text-gray-500">
            Boekingsverificatie en automatisch doorsturen naar BAS
          </p>
        </div>
        <div className="flex items-center gap-1.5 px-3 py-1.5 bg-green-50 border border-green-200 rounded-lg text-xs text-green-700">
          <svg className="w-3.5 h-3.5 shrink-0" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
          </svg>
          PDF's lokaal verwerkt — niet naar server gestuurd
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-gray-200 px-8">
        {[
          { id: 'vergelijken', label: 'Vergelijken' },
          { id: 'email',       label: 'E-mail' },
          { id: 'naar-bas',    label: 'Naar BAS XML' },
        ].map(t => (
          <button key={t.id} onClick={() => setActiveTab(t.id)}
            className={`px-4 py-3 text-sm font-medium border-b-2 -mb-px ${
              activeTab === t.id
                ? 'text-gray-900 border-b-2'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
            style={activeTab === t.id ? { borderColor: 'var(--brand)' } : {}}>
            {t.label}
          </button>
        ))}
      </div>

      {/* ── Tab: Naar BAS ─────────────────────────────────────────────── */}
      {activeTab === 'naar-bas' && (
        <div className="p-8 max-w-5xl mx-auto space-y-5">
          {/* Auto sync card */}
          <div className="pro-card p-5">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h3 className="font-semibold text-gray-900">Automatisch verwerken via e-mail</h3>
                <p className="text-sm text-gray-500 mt-1">
                  Stuur de Corendon bevestiging naar de map <strong>"{reisbescheidenFolder}"</strong> in uw mailbox.
                  TravelAIgent haalt het PDF op, zet het om naar BAS XML en stuurt het door naar uw BAS-e-mailadres.
                </p>
                {basNumber
                  ? <p className="text-xs text-blue-600 mt-2">Wordt verstuurd naar: <strong>{basNumber}@bas.nl</strong></p>
                  : <p className="text-xs text-amber-600 mt-2"><span className="text-amber-600">&#9888;</span> Stel uw BAS-nummer in via Instellingen → Reisbescheiden</p>}
              </div>
              <button onClick={handleSyncImap} disabled={syncing}
                className="btn-primary shrink-0 flex items-center gap-2">
                {syncing ? <span className="spinner" style={{ width: 14, height: 14, borderWidth: 2 }} /> : null}
                {syncing ? 'Bezig…' : 'Nu synchroniseren'}
              </button>
            </div>
            {syncResult && (
              <div className={`mt-4 px-4 py-3 rounded-lg text-sm ${syncResult.ok ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-700'}`}>
                {syncResult.ok ? '✓ ' : '✗ '}{syncResult.msg}
                {syncResult.details?.length > 0 && (
                  <ul className="mt-1 ml-4 list-disc text-xs opacity-70">
                    {syncResult.details.map((d, i) => <li key={i}>{d}</li>)}
                  </ul>
                )}
              </div>
            )}
          </div>

          {/* Divider */}
          <div className="flex items-center gap-3 text-xs text-gray-400">
            <div className="flex-1 h-px bg-gray-200" />
            of verwerk handmatig
            <div className="flex-1 h-px bg-gray-200" />
          </div>

          {/* Manual converter */}
          <div className="pro-card p-5 space-y-4">
            <h3 className="font-semibold text-gray-900">Handmatig omzetten</h3>

            {/* Drop zone */}
            <div
              className={`border border-dashed rounded-lg p-6 flex flex-col items-center gap-3 cursor-pointer transition-all ${
                convertFile ? 'border-blue-200 bg-blue-50' : 'border-gray-300 hover:border-gray-400 bg-gray-50'
              } ${convertError ? 'border-red-300 bg-red-50' : ''}`}
              onClick={() => convertInputRef.current?.click()}
              onDragOver={e => e.preventDefault()}
              onDrop={e => { e.preventDefault(); const f = e.dataTransfer.files[0]; if (f) handleConvertFile(f); }}
            >
              <input ref={convertInputRef} type="file" accept=".pdf,.eml,.html,.htm" className="hidden"
                onChange={e => { if (e.target.files[0]) handleConvertFile(e.target.files[0]); }} />
              {convertLoading ? (
                <><span className="spinner" style={{ width: 28, height: 28, borderWidth: 3 }} /><p className="text-sm text-gray-500">PDF verwerken…</p></>
              ) : convertFile ? (
                <div className="text-center space-y-1">
                  <p className="text-sm font-medium text-gray-700">{convertFile.name}</p>
                  {convertError
                    ? <p className="text-xs text-red-600">{convertError}</p>
                    : convertParsed
                      ? <p className="text-xs text-green-600 font-medium">✓ Corendon bevestiging ingelezen · {convertParsed.travelers.length} reizigers · res. {convertParsed.reservationNumber}</p>
                      : null}
                  <button onClick={e => { e.stopPropagation(); handleConvertFile(null); }} className="text-xs text-gray-400 hover:text-red-500 underline">Verwijder</button>
                </div>
              ) : (
                <>
                  <svg className="w-8 h-8 text-gray-300 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 13h6m-3-3v6m5 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <p className="text-sm font-semibold text-gray-600">Corendon bevestiging (PDF)</p>
                  <p className="text-xs text-gray-400">Sleep een bestand hierheen of klik om te bladeren</p>
                  <p className="text-xs text-gray-300">PDF of e-mail (.eml, .html)</p>
                </>
              )}
            </div>

            {/* E-mail metadata banner (shown when an .eml was loaded) */}
            {convertEmailMeta && (
              <div className="flex flex-col gap-1 px-4 py-3 bg-blue-50 border border-blue-200 rounded-lg text-xs text-blue-800">
                <p className="font-semibold text-blue-900">E-mail metadata</p>
                {convertEmailMeta.from    && <p><span className="text-blue-500 w-16 inline-block">Van</span>{convertEmailMeta.from}</p>}
                {convertEmailMeta.subject && <p><span className="text-blue-500 w-16 inline-block">Onderwerp</span>{convertEmailMeta.subject}</p>}
                {convertEmailMeta.date    && <p><span className="text-blue-500 w-16 inline-block">Datum</span>{new Date(convertEmailMeta.date).toLocaleString('nl-NL')}</p>}
              </div>
            )}

            {/* Step 2: Compute */}
            {convertParsed && !generatedXml && (
              <button onClick={handleGenerateXml}
                className="w-full btn-primary py-3 flex items-center justify-center gap-2">
                Omzetten naar BAS XML
              </button>
            )}

            {/* Step 3: XML preview + actions */}
            {generatedXml && (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-semibold text-gray-700">XML gegenereerd</p>
                  <button onClick={() => { setGeneratedXml(''); setSendResult(null); }} className="text-xs text-gray-400 hover:text-gray-600 underline">Opnieuw genereren</button>
                </div>

                {/* XML preview */}
                <pre className="bg-gray-900 text-green-300 text-xs rounded-lg p-4 overflow-x-auto max-h-64 overflow-y-auto leading-relaxed">
                  {generatedXml}
                </pre>

                {/* Flexible send-to email */}
                <div className="space-y-2">
                  <label className="label text-xs">Versturen naar</label>
                  <div className="flex gap-2 flex-wrap">
                    {basNumber && (
                      <button onClick={() => setSendToEmail(`${basNumber}@bas.nl`)}
                        className={`px-3 py-1.5 text-xs rounded-lg border transition-all ${sendToEmail === `${basNumber}@bas.nl` ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-600 border-gray-200 hover:border-blue-300'}`}>
                        BAS ({basNumber}@bas.nl)
                      </button>
                    )}
                    {imapUser && (
                      <button onClick={() => setSendToEmail(imapUser)}
                        className={`px-3 py-1.5 text-xs rounded-lg border transition-all ${sendToEmail === imapUser ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-600 border-gray-200 hover:border-blue-300'}`}>
                        Eigen inbox ({imapUser})
                      </button>
                    )}
                  </div>
                  <input
                    className="input text-sm"
                    placeholder="of voer een e-mailadres in…"
                    value={sendToEmail}
                    onChange={e => setSendToEmail(e.target.value)}
                  />
                </div>

                <div className="flex gap-2">
                  <button onClick={handleDownloadXml}
                    className="btn-secondary flex items-center justify-center gap-1.5">
                    Download
                  </button>
                  <button onClick={handleSendEmail} disabled={sending || !sendToEmail.trim()}
                    className="flex-1 btn-primary flex items-center justify-center gap-1.5 disabled:opacity-40">
                    {sending
                      ? <><span className="spinner" style={{ width: 14, height: 14, borderWidth: 2 }} /> Versturen…</>
                      : `Verstuur naar ${sendToEmail || '…'}`}
                  </button>
                </div>

                {sendResult && (
                  <div className={`px-4 py-3 rounded-lg text-sm ${sendResult.ok ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-700'}`}>
                    {sendResult.msg}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Tab: Vergelijken ──────────────────────────────────────────── */}
      {activeTab === 'vergelijken' && (
        <div className="p-8 max-w-5xl mx-auto space-y-5">
          {/* Drop zones */}
          <div className="pro-card p-5">
          <h3 className="text-sm font-semibold text-gray-800 mb-4">Bestanden vergelijken</h3>
          <div className="flex gap-3 items-stretch">
            <DropZone
              label="BAS Bevestiging (PDF)"
              accept=".pdf"
              file={leftFile}
              loading={loadingL}
              error={errorL}
              parsed={leftParsed}
              onFile={handleLeftFile}
              accentColor="blue"
            />
            <div className="flex items-center justify-center text-gray-300 text-xl shrink-0 select-none">⇄</div>
            <DropZone
              label="Operator Bevestiging (PDF / EML / XML)"
              accept=".pdf,.eml,.html,.htm,.xml,.txt"
              file={rightFile}
              loading={loadingR}
              error={errorR}
              parsed={rightParsed}
              onFile={handleRightFile}
              accentColor="purple"
            />
          </div>
          </div>

          {/* Right-side e-mail metadata banner */}
          {rightEmailMeta && (
            <div className="flex flex-wrap gap-4 px-4 py-3 bg-purple-50 border border-purple-200 rounded-lg text-xs text-purple-800">
              <p className="font-semibold text-purple-900 w-full">Operator e-mail metadata</p>
              {rightEmailMeta.from    && <p><span className="text-purple-400 mr-1">Van</span>{rightEmailMeta.from}</p>}
              {rightEmailMeta.subject && <p><span className="text-purple-400 mr-1">Onderwerp</span>{rightEmailMeta.subject}</p>}
              {rightEmailMeta.date    && <p><span className="text-purple-400 mr-1">Datum</span>{new Date(rightEmailMeta.date).toLocaleString('nl-NL')}</p>}
            </div>
          )}

          {/* Parsed summaries */}
          {(leftParsed || rightParsed) && (
            <div className="flex gap-3">
              {leftParsed && (
                <div className="flex-1 px-4 py-3 bg-blue-50 border border-blue-100 rounded-lg text-xs text-blue-800 space-y-0.5">
                  <p className="font-semibold">BAS ingelezen ✓</p>
                  <p>{leftParsed.travelers.length} reiziger(s) · {leftParsed.hotel || '?'} · {leftParsed.departureDate || '?'}</p>
                  {leftParsed.reservationNumber && <p>Res. {leftParsed.reservationNumber}{leftParsed.pnr ? ` · PNR ${leftParsed.pnr}` : ''}</p>}
                </div>
              )}
              {rightParsed && (
                <div className="flex-1 px-4 py-3 bg-purple-50 border border-purple-100 rounded-lg text-xs text-purple-800 space-y-0.5">
                  <p className="font-semibold">{rightParsed._format} ingelezen ✓</p>
                  <p>{rightParsed.travelers.length} reiziger(s) · {rightParsed.hotel || '?'} · {rightParsed.departureDate || '?'}</p>
                  {rightParsed.reservationNumber && <p>Res. {rightParsed.reservationNumber}{rightParsed.pnr ? ` · PNR ${rightParsed.pnr}` : ''}</p>}
                </div>
              )}
            </div>
          )}

          {/* Compare button */}
          <button
            onClick={handleCompare}
            disabled={!canCompare}
            className="w-full btn-primary py-3 text-base flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed">
            {comparing
              ? <><span className="spinner" style={{ width: 18, height: 18, borderWidth: 2 }} /> Bezig met vergelijken…</>
              : 'Vergelijk bevestigingen'}
          </button>

          {/* Results */}
          {diffResult && (
            <div className="pro-card overflow-hidden">

              {/* ── Summary bar ─────────────────────────────────────────── */}
              <div className={`px-5 py-4 border-b ${isPass ? 'bg-green-50' : 'bg-red-50'}`}>
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <p className={`font-semibold text-base ${isPass ? 'text-green-800' : 'text-red-800'}`}>
                      {isPass
                        ? 'PASS — Bevestigingen komen overeen'
                        : `FAIL — ${blockingCount} blokkerende afwijking${blockingCount !== 1 ? 'en' : ''}`}
                    </p>
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      {errorCount > 0 && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">
                          {errorCount} fout{errorCount !== 1 ? 'en' : ''}
                        </span>
                      )}
                      {uncertainCount > 0 && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-700">
                          {uncertainCount} onzeker
                        </span>
                      )}
                      {warningCount > 0 && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
                          {warningCount} waarschuwing{warningCount !== 1 ? 'en' : ''}
                        </span>
                      )}
                      {correctCount > 0 && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
                          {correctCount} correct
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2 shrink-0">
                    <button onClick={() => setDrawerOpen(true)}
                      className="btn-secondary text-xs flex items-center gap-1.5">
                      Brondata
                    </button>
                    <button onClick={handleCompare}
                      className="btn-secondary text-xs flex items-center gap-1.5">
                      Opnieuw
                    </button>
                    <button onClick={handleReset}
                      className="btn-secondary text-xs flex items-center gap-1.5">
                      Nieuw
                    </button>
                    <button
                      onClick={() => downloadReport(diffResult, leftParsed, rightParsed, user?.name || user?.email, rightParsed?._format || 'Operator')}
                      className="btn-secondary text-xs flex items-center gap-1.5">
                      Download rapport
                    </button>
                    <button
                      onClick={handleReset}
                      className="text-xs px-3 py-1.5 rounded-lg border border-green-400 bg-green-50 text-green-700 hover:bg-green-100 font-medium transition-all flex items-center gap-1.5">
                      ✓ Bevestig
                    </button>
                  </div>
                </div>
              </div>

              {/* ── Problem-first issues list ────────────────────────────── */}
              {(() => {
                const visibleIssues = issues.filter(i => !confirmedWarnings.has(i.id));
                return visibleIssues.length > 0 ? (
                <div className="px-5 py-4 border-b">
                  <div className="flex items-center justify-between mb-3">
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide">
                      Afwijkingen ({visibleIssues.length})
                    </p>
                    {reviewedFields.size > 0 && (
                      <button onClick={() => setReviewedFields(new Set())}
                        className="text-xs text-gray-400 hover:text-gray-600 underline">
                        Wis beoordeeld
                      </button>
                    )}
                  </div>
                  <div className="space-y-1.5">
                    {visibleIssues.map(issue => {
                      const reviewed = reviewedFields.has(issue.id);
                      const sc = ({
                        error:     { dot: 'bg-red-500',    bg: 'bg-red-50',    border: 'border-red-100'    },
                        uncertain: { dot: 'bg-orange-400', bg: 'bg-orange-50', border: 'border-orange-100' },
                        warning:   { dot: 'bg-amber-400',  bg: 'bg-amber-50',  border: 'border-amber-100'  },
                      })[issue.severity] ?? { dot: 'bg-gray-300', bg: 'bg-gray-50', border: 'border-gray-100' };
                      return (
                        <div key={issue.id}
                          className={`flex items-center gap-3 px-3 py-2.5 rounded-lg border cursor-pointer transition-all hover:opacity-80 ${
                            reviewed ? 'opacity-40 bg-gray-50 border-gray-100' : `${sc.bg} ${sc.border}`
                          }`}
                          onClick={() => {
                            if (!detailsOpen) setDetailsOpen(true);
                            setTimeout(() => {
                              const el = document.getElementById(`detail-${issue.id}`);
                              if (!el) return;
                              el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                              el.classList.add('ring-2', 'ring-inset', 'ring-blue-400');
                              setTimeout(() => el.classList.remove('ring-2', 'ring-inset', 'ring-blue-400'), 1800);
                            }, 80);
                          }}>
                          <span className={`w-2 h-2 rounded-full shrink-0 ${sc.dot}`} />
                          <div className="flex-1 min-w-0 text-sm">
                            <span className="font-medium text-gray-800">{issue.label}</span>
                            {issue.description && (
                              <span className="text-gray-500 ml-2 text-xs truncate">{issue.description}</span>
                            )}
                          </div>
                          {issue.severity === 'warning' && (
                            <button
                              onClick={e => {
                                e.stopPropagation();
                                setConfirmedWarnings(prev => new Set([...prev, issue.id]));
                              }}
                              className="shrink-0 text-xs px-2 py-0.5 rounded border border-green-300 bg-green-50 text-green-700 hover:bg-green-100 transition-all">
                              Bevestig
                            </button>
                          )}
                          <button
                            onClick={e => { e.stopPropagation(); toggleReviewed(issue.id); }}
                            className={`shrink-0 text-xs px-2 py-0.5 rounded border transition-all ${
                              reviewed
                                ? 'border-green-300 bg-green-50 text-green-700'
                                : 'border-gray-200 bg-white text-gray-400 hover:border-green-300 hover:text-green-600'
                            }`}>
                            {reviewed ? '✓ Gezien' : 'Markeer'}
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>
                ) : (
                <div className="px-5 py-5 text-center border-b">
                  <p className="text-green-700 font-medium text-sm">
                    Alle velden komen overeen — geen actie vereist.
                  </p>
                </div>
                );
              })()}

              {/* ── Expand / collapse full detail ───────────────────────── */}
              <button
                onClick={() => setDetailsOpen(o => !o)}
                className="w-full flex items-center justify-between px-5 py-3 bg-gray-50 border-b text-xs font-semibold text-gray-400 uppercase tracking-wide hover:bg-gray-100 transition-colors">
                <span>Volledige vergelijking</span>
                <span>{detailsOpen ? '▲' : '▼'}</span>
              </button>

              {detailsOpen && (
                <>
                  {/* Travelers section */}
                  <div className="px-5 py-4 border-b">
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">Reizigers</p>
                    {diffResult.travelers?.details?.map((t, i) => {
                      const cfg = ({
                        correct:   { bg: '',              icon: '✓', color: 'text-green-500'  },
                        warning:   { bg: 'bg-amber-50',   icon: '⚠', color: 'text-amber-500' },
                        uncertain: { bg: 'bg-orange-50',  icon: '?', color: 'text-orange-500' },
                        error:     { bg: 'bg-red-50',     icon: '✗', color: 'text-red-500'   },
                      })[t.severity] ?? { bg: '', icon: '✓', color: 'text-green-500' };
                      return (
                        <div key={i} id={`detail-traveler-${i}`}
                          className={`flex items-center gap-3 py-2 px-3 rounded-lg mb-1 transition-all ${cfg.bg}`}>
                          <span className={`font-bold text-lg w-5 text-center shrink-0 ${cfg.color}`}>{cfg.icon}</span>
                          <div className="flex-1 grid grid-cols-2 gap-4 text-sm">
                            <span className="text-gray-700">
                              {t.left ? `${t.left.firstName} ${t.left.lastName}${t.left.dob ? ` (${t.left.dob})` : ''}` : '—'}
                            </span>
                            <span className="text-gray-700">
                              {t.right ? `${t.right.firstName} ${t.right.lastName}${t.right.dob ? ` (${t.right.dob})` : ''}` : '—'}
                            </span>
                          </div>
                          {t.detail && (
                            <span className={`text-xs shrink-0 ${cfg.color}`}>{t.detail}</span>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  {/* Field comparison table */}
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-gray-50 border-b text-xs text-gray-500 uppercase tracking-wide">
                          <th className="text-left px-5 py-2.5 font-medium w-44">Veld</th>
                          <th className="text-left px-4 py-2.5 font-medium">BAS</th>
                          <th className="text-left px-4 py-2.5 font-medium">{rightParsed?._format || 'Operator'}</th>
                          <th className="px-4 py-2.5 font-medium text-center w-16">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {Object.entries(FIELD_LABELS).map(([key, label]) => {
                          const entry = diffResult[key];
                          if (!entry) return null;
                          const sev = entry.severity;
                          const rowBg   = sev === 'error'    ? 'bg-red-50'
                                        : sev === 'uncertain' ? 'bg-orange-50'
                                        : sev === 'warning'   ? 'bg-amber-50'
                                        : '';
                          const textCls = sev === 'error'    ? 'text-red-900 font-medium'
                                        : sev === 'uncertain' ? 'text-orange-900 font-medium'
                                        : sev === 'warning'   ? 'text-amber-900 font-medium'
                                        : 'text-gray-600';
                          const icon    = sev === 'error'    ? <span className="text-red-500 text-lg">✗</span>
                                        : sev === 'uncertain' ? <span className="text-orange-500 text-lg font-bold">?</span>
                                        : sev === 'warning'   ? <span className="text-amber-500 text-lg">⚠</span>
                                        : <span className="text-green-500 text-lg">✓</span>;
                          return (
                            <tr key={key} id={`detail-field-${key}`}
                              className={`border-b transition-all ${rowBg}`}>
                              <td className="px-5 py-2.5 font-medium text-gray-600 text-sm">{label}</td>
                              <td className={`px-4 py-2.5 ${textCls}`}>{formatVal(key, entry.left)}</td>
                              <td className={`px-4 py-2.5 ${textCls}`}>{formatVal(key, entry.right)}</td>
                              <td className="px-4 py-2.5 text-center">
                                {icon}
                                {entry.detail && <div className="text-xs text-gray-400 mt-0.5">{entry.detail}</div>}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      )}

      {/* ── Tab: E-mail ────────────────────────────────────────────────── */}
      {activeTab === 'email' && (
        <div className="p-8 max-w-5xl mx-auto space-y-5">
          {/* Two-column layout: BAS left, inbox right */}
          <div className="flex gap-4 items-start">

            {/* Left: BAS PDF upload */}
            <div className="flex-1 space-y-3">
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide">BAS Bevestiging</p>
              <DropZone
                label="BAS Bevestiging (PDF)"
                accept=".pdf"
                file={emailLeftFile}
                loading={emailLoadingL}
                error={emailErrorL}
                parsed={emailLeftParsed}
                onFile={handleEmailLeftFile}
                accentColor="blue"
              />
              {emailLeftParsed && (
                <div className="px-4 py-3 bg-blue-50 border border-blue-100 rounded-lg text-xs text-blue-800 space-y-0.5">
                  <p className="font-semibold">BAS ingelezen ✓</p>
                  <p>{emailLeftParsed.travelers?.length} reiziger(s) · {emailLeftParsed.hotel || '?'} · {emailLeftParsed.departureDate || '?'}</p>
                  {emailLeftParsed.reservationNumber && <p>Res. {emailLeftParsed.reservationNumber}</p>}
                </div>
              )}
            </div>

            <div className="flex items-center justify-center text-gray-300 text-xl shrink-0 pt-8 select-none">⇄</div>

            {/* Right: inbox email picker */}
            <div className="flex-1 space-y-3">
              <div className="flex items-center justify-between">
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide">
                  {reisbescheidenFolder || 'Reisbescheiden'} map
                </p>
                <button
                  onClick={fetchInboxEmails}
                  disabled={inboxLoading}
                  className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1 disabled:opacity-40">
                  {inboxLoading
                    ? <span className="spinner" style={{ width: 12, height: 12, borderWidth: 2 }} />
                    : null}
                  Vernieuwen
                </button>
              </div>

              {inboxError && (
                <div className="px-3 py-2 bg-red-50 border border-red-200 rounded-lg text-xs text-red-700">
                  {inboxError}
                </div>
              )}

              {/* Email list */}
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                {inboxLoading && inboxEmails.length === 0 ? (
                  <div className="flex items-center justify-center gap-2 py-10 text-gray-400 text-sm">
                    <span className="spinner" style={{ width: 18, height: 18, borderWidth: 2 }} />
                    Bezig met ophalen…
                  </div>
                ) : inboxEmails.length === 0 ? (
                  <div className="py-10 text-center text-sm text-gray-400">
                    <p>Geen berichten gevonden in <strong>{reisbescheidenFolder || 'Reisbescheiden'}</strong></p>
                    <p className="text-xs mt-1 text-gray-300">Stuur een Corendon bevestiging naar deze map en klik vernieuwen</p>
                  </div>
                ) : (
                  <div className="divide-y divide-gray-100 max-h-72 overflow-y-auto">
                    {inboxEmails.map(email => {
                      const isSelected = selectedEmailUid === email.uid;
                      const isLoading  = isSelected && selectedEmailLoading;
                      return (
                        <button
                          key={email.uid}
                          onClick={() => handleSelectEmail(email.uid)}
                          className={`w-full text-left px-4 py-3 transition-all hover:bg-gray-50 flex items-start gap-3 ${
                            isSelected ? 'bg-purple-50 border-l-2 border-purple-400' : ''
                          }`}>
                          <span className="mt-0.5 shrink-0 text-base">
                            {isLoading ? <span className="spinner inline-block" style={{ width: 14, height: 14, borderWidth: 2 }} /> : isSelected ? '✓' : null}
                          </span>
                          <div className="flex-1 min-w-0">
                            <p className={`text-sm truncate ${!email.seen ? 'font-semibold text-gray-900' : 'text-gray-700'}`}>
                              {email.subject}
                            </p>
                            <p className="text-xs text-gray-400 truncate mt-0.5">
                              {email.fromName || email.from}
                              {email.date && (
                                <span className="ml-2">
                                  {new Date(email.date).toLocaleDateString('nl-NL', { day: 'numeric', month: 'short' })}
                                </span>
                              )}
                              {email.hasPdf && <span className="ml-2 text-blue-400">PDF</span>}
                              {!email.seen && <span className="ml-2 inline-block w-1.5 h-1.5 rounded-full bg-blue-500 align-middle" />}
                            </p>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Selected email preview */}
              {selectedEmailParsed && (
                <div className="px-4 py-3 bg-purple-50 border border-purple-100 rounded-lg text-xs text-purple-800 space-y-0.5">
                  <p className="font-semibold">Corendon e-mail ingelezen ✓</p>
                  {selectedEmailMeta?.subject && <p className="truncate">{selectedEmailMeta.subject}</p>}
                  <p>{selectedEmailParsed.travelers?.length} reiziger(s) · {selectedEmailParsed.hotel || '?'} · {selectedEmailParsed.departureDate || '?'}</p>
                  {selectedEmailParsed.reservationNumber && <p>Res. {selectedEmailParsed.reservationNumber}</p>}
                </div>
              )}
            </div>
          </div>

          {/* Compare button */}
          <button
            onClick={handleEmailCompare}
            disabled={!emailLeftParsed || !selectedEmailParsed || emailComparing}
            className="w-full btn-primary py-3 text-base flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed">
            {emailComparing
              ? <><span className="spinner" style={{ width: 18, height: 18, borderWidth: 2 }} /> Bezig met vergelijken…</>
              : 'Vergelijk bevestigingen'}
          </button>

          {/* Results — reuse the same comparison UI */}
          {emailDiffResult && (() => {
            const isEmailPass = emailDiffResult.verdict === 'PASS';
            let eErr = 0, eWarn = 0, eUnc = 0, eCorr = 0;
            for (const key of Object.keys(FIELD_LABELS)) {
              const s = emailDiffResult[key]?.severity;
              if (s === 'error') eErr++;
              else if (s === 'uncertain') eUnc++;
              else if (s === 'warning') eWarn++;
              else if (s === 'correct') eCorr++;
            }
            if (emailDiffResult.travelers) {
              const s = emailDiffResult.travelers.severity;
              if (s === 'error') eErr++;
              else if (s === 'uncertain') eUnc++;
              else if (s === 'warning') eWarn++;
              else eCorr++;
            }
            const eBlocking = eErr + eUnc;
            const eIssues = [];
            for (const [i, t] of (emailDiffResult.travelers?.details || []).entries()) {
              if (t.severity !== 'correct') eIssues.push({ id: `e-traveler-${i}`, severity: t.severity, label: 'Reiziger', description: t.detail });
            }
            for (const [key, label] of Object.entries(FIELD_LABELS)) {
              const entry = emailDiffResult[key];
              if (entry && entry.severity !== 'correct') {
                eIssues.push({ id: `e-field-${key}`, severity: entry.severity, label, description: entry.detail || (entry.left != null && entry.right != null ? `"${String(entry.left)}" ≠ "${String(entry.right)}"` : entry.left == null ? 'Ontbreekt in BAS' : 'Ontbreekt in e-mail') });
              }
            }
            const sevRank = { error: 0, uncertain: 1, warning: 2 };
            eIssues.sort((a, b) => (sevRank[a.severity] ?? 3) - (sevRank[b.severity] ?? 3));
            return (
              <div className="pro-card overflow-hidden">
                <div className={`px-5 py-4 border-b ${isEmailPass ? 'bg-green-50' : 'bg-red-50'}`}>
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <p className={`font-semibold text-base ${isEmailPass ? 'text-green-800' : 'text-red-800'}`}>
                        {isEmailPass ? 'PASS — Bevestigingen komen overeen' : `FAIL — ${eBlocking} blokkerende afwijking${eBlocking !== 1 ? 'en' : ''}`}
                      </p>
                      <div className="flex flex-wrap gap-1.5 mt-2">
                        {eErr > 0 && <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">{eErr} fout{eErr !== 1 ? 'en' : ''}</span>}
                        {eUnc > 0 && <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-700">{eUnc} onzeker</span>}
                        {eWarn > 0 && <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700">{eWarn} waarschuwing{eWarn !== 1 ? 'en' : ''}</span>}
                        {eCorr > 0 && <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">{eCorr} correct</span>}
                      </div>
                    </div>
                    <div className="flex gap-2 flex-wrap">
                      <button onClick={() => downloadReport(emailDiffResult, emailLeftParsed, selectedEmailParsed, user?.name || user?.email, 'Corendon E-mail')}
                        className="btn-secondary text-xs flex items-center gap-1.5">Download rapport</button>
                      <button onClick={() => { setEmailDiffResult(null); setSelectedEmailParsed(null); setSelectedEmailUid(null); setSelectedEmailMeta(null); setEmailConfirmedWarnings(new Set()); }}
                        className="btn-secondary text-xs flex items-center gap-1.5">Nieuw</button>
                      <button onClick={handleEmailBevestig}
                        className="text-xs px-3 py-1.5 rounded-lg border border-green-400 bg-green-50 text-green-700 hover:bg-green-100 font-medium transition-all flex items-center gap-1.5">
                        ✓ Bevestig
                      </button>
                    </div>
                  </div>
                </div>

                {(() => {
                  const visibleEIssues = eIssues.filter(i => !emailConfirmedWarnings.has(i.id));
                  return visibleEIssues.length > 0 ? (
                  <div className="px-5 py-4 border-b">
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">Afwijkingen ({visibleEIssues.length})</p>
                    <div className="space-y-1.5">
                      {visibleEIssues.map(issue => {
                        const sc = ({ error: { dot: 'bg-red-500', bg: 'bg-red-50', border: 'border-red-100' }, uncertain: { dot: 'bg-orange-400', bg: 'bg-orange-50', border: 'border-orange-100' }, warning: { dot: 'bg-amber-400', bg: 'bg-amber-50', border: 'border-amber-100' } })[issue.severity] ?? { dot: 'bg-gray-300', bg: 'bg-gray-50', border: 'border-gray-100' };
                        return (
                          <div key={issue.id} className={`flex items-center gap-3 px-3 py-2.5 rounded-lg border ${sc.bg} ${sc.border}`}>
                            <span className={`w-2 h-2 rounded-full shrink-0 ${sc.dot}`} />
                            <div className="flex-1 min-w-0 text-sm">
                              <span className="font-medium text-gray-800">{issue.label}</span>
                              {issue.description && <span className="text-gray-500 ml-2 text-xs">{issue.description}</span>}
                            </div>
                            {issue.severity === 'warning' && (
                              <button
                                onClick={() => setEmailConfirmedWarnings(prev => new Set([...prev, issue.id]))}
                                className="shrink-0 text-xs px-2 py-0.5 rounded border border-green-300 bg-green-50 text-green-700 hover:bg-green-100 transition-all">
                                Bevestig
                              </button>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                  ) : (
                  <div className="px-5 py-5 text-center">
                    <p className="text-green-700 font-medium text-sm">Alle velden komen overeen — geen actie vereist.</p>
                  </div>
                  );
                })()}

                <button onClick={() => setEmailDetailsOpen(o => !o)}
                  className="w-full flex items-center justify-between px-5 py-3 bg-gray-50 border-b text-xs font-semibold text-gray-400 uppercase tracking-wide hover:bg-gray-100 transition-colors">
                  <span>Volledige vergelijking</span>
                  <span>{emailDetailsOpen ? '▲' : '▼'}</span>
                </button>

                {emailDetailsOpen && (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-gray-50 border-b text-xs text-gray-500 uppercase tracking-wide">
                          <th className="text-left px-5 py-2.5 font-medium w-44">Veld</th>
                          <th className="text-left px-4 py-2.5 font-medium">BAS</th>
                          <th className="text-left px-4 py-2.5 font-medium">Corendon E-mail</th>
                          <th className="px-4 py-2.5 font-medium text-center w-16">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {Object.entries(FIELD_LABELS).map(([key, label]) => {
                          const entry = emailDiffResult[key];
                          if (!entry) return null;
                          const sev = entry.severity;
                          const rowBg = sev === 'error' ? 'bg-red-50' : sev === 'uncertain' ? 'bg-orange-50' : sev === 'warning' ? 'bg-amber-50' : '';
                          const textCls = sev === 'error' ? 'text-red-900 font-medium' : sev === 'uncertain' ? 'text-orange-900 font-medium' : sev === 'warning' ? 'text-amber-900 font-medium' : 'text-gray-600';
                          const icon = sev === 'error' ? <span className="text-red-500 text-lg">✗</span> : sev === 'uncertain' ? <span className="text-orange-500 text-lg font-bold">?</span> : sev === 'warning' ? <span className="text-amber-500 text-lg">⚠</span> : <span className="text-green-500 text-lg">✓</span>;
                          return (
                            <tr key={key} className={`border-b ${rowBg}`}>
                              <td className="px-5 py-2.5 font-medium text-gray-600 text-sm">{label}</td>
                              <td className={`px-4 py-2.5 ${textCls}`}>{formatVal(key, entry.left)}</td>
                              <td className={`px-4 py-2.5 ${textCls}`}>{formatVal(key, entry.right)}</td>
                              <td className="px-4 py-2.5 text-center">{icon}{entry.detail && <div className="text-xs text-gray-400 mt-0.5">{entry.detail}</div>}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            );
          })()}
        </div>
      )}

      {/* ── Source transparency drawer ─────────────────────────────────── */}
      {drawerOpen && (
        <div className="fixed inset-0 z-50 flex justify-end">
          <div className="absolute inset-0 bg-black/20" onClick={() => setDrawerOpen(false)} />
          <div className="relative w-full max-w-sm bg-white h-full overflow-y-auto shadow-2xl flex flex-col">
            <div className="flex items-center justify-between px-5 py-4 border-b sticky top-0 bg-white z-10">
              <h3 className="font-semibold text-gray-900 text-sm">Brondata ingelezen velden</h3>
              <button onClick={() => setDrawerOpen(false)}
                className="text-gray-400 hover:text-gray-600 w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 text-lg leading-none">
                ✕
              </button>
            </div>
            <div className="p-5 space-y-6 flex-1">
              <div>
                <p className="text-xs font-semibold text-blue-700 uppercase tracking-wide mb-3">
                  BAS — {leftParsed?._format || 'BAS'}
                </p>
                <DrawerFieldList parsed={leftParsed} />
              </div>
              <hr className="border-gray-100" />
              <div>
                <p className="text-xs font-semibold text-purple-700 uppercase tracking-wide mb-3">
                  {rightParsed?._format || 'Operator'}
                </p>
                <DrawerFieldList parsed={rightParsed} />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function Reisbescheiden() {
  const { hasAccess } = useSubscription();
  if (!hasAccess('bureau')) return <LockedPage featureName="Reisbescheiden"><ReisbescheidenInner /></LockedPage>;
  return <ReisbescheidenInner />;
}
